import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Edit, Trash2, Eye, Search, Wrench, Shield, Copy, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Package, ChevronRight as ChevronExpand, Calendar, UserPlus, Users, FolderPlus, FolderOpen, History, Bell, ArrowRight, RotateCcw, X, Filter, PackageCheck } from "lucide-react";
import axios from "axios";
import api from "../../services/apiService";
import { toast } from "react-toastify";
import EquipmentModal from "./EquipmentModal";
import EquipmentTypeModal from "./EquipmentTypeModal";
import ModelModal from "./ModelModal";
import ConfirmationModal from "./ConfirmationModal";
import InspectionModal from "./InspectionModal";
import AssignmentModal from "./AssignmentModal";
import GroupModal from "./GroupModal";
import HistoryModal from "./HistoryModal";
import EquipmentCheckModal from "./EquipmentCheckModal";
import InspectionNotification from "./InspectionNotification";
import ServiceModal from "./ServiceModal";
import ImportReportModal from "./ImportReportModal";
import { UserContext } from "../../contexts/UserContext";
import MultiSelectDropdown from "../common/MultiSelectDropdown";
import { fetchWarehouseItemsForEquipment, convertWarehouseItemToEquipment } from "../../services/warehouseEquipmentIntegration";

const EquipmentManagementNoGroup = () => {
  const { user } = useContext(UserContext);
  const location = useLocation();
  console.log("contextuserrrrrr", user)

  // Helper function to darken a hex color for text (creates contrast)
  const darkenColor = (hex, percent = 40) => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Parse RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Darken
    r = Math.max(0, Math.floor(r * (1 - percent / 100)));
    g = Math.max(0, Math.floor(g * (1 - percent / 100)));
    b = Math.max(0, Math.floor(b * (1 - percent / 100)));

    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // Default hex colors
  const defaultColors = {
    expired: '#FEE2E2',      // red-100
    firstCheck: '#FFEDD5',   // orange-100
    secondCheck: '#FEF9C3',  // yellow-100
    valid: '#DCFCE7'         // green-100
  };

  // Helper functions for user-configurable validity colors - returns style object
  // Only colors expired/warning items, valid items have no special styling
  const getInspectionValidityStyle = (daysUntilExpiry, inspectionStatus = null) => {
    const settings = {
      expiredColor: user?.ppe_inspection_expired_color || defaultColors.expired,
      firstCheck: user?.ppe_inspection_first_check || 30,
      firstCheckColor: user?.ppe_inspection_first_check_color || defaultColors.firstCheck,
      secondCheck: user?.ppe_inspection_second_check || 60,
      secondCheckColor: user?.ppe_inspection_second_check_color || defaultColors.secondCheck
    };

    let bgColor = null;

    // Failed inspection always red
    if (inspectionStatus === 'non-ok') {
      bgColor = settings.expiredColor;
    } else if (daysUntilExpiry < 0) {
      // Expired (negative days)
      bgColor = settings.expiredColor;
    } else if (daysUntilExpiry <= settings.firstCheck) {
      // Within first threshold
      bgColor = settings.firstCheckColor;
    } else if (daysUntilExpiry <= settings.secondCheck) {
      // Within second threshold
      bgColor = settings.secondCheckColor;
    }

    // Valid items (beyond second threshold) - no special styling
    if (!bgColor) {
      return {};
    }

    return {
      backgroundColor: bgColor,
      color: darkenColor(bgColor, 50)
    };
  };

  // Legacy function for backward compatibility - returns style object
  const getInspectionValidityColor = (daysUntilExpiry, inspectionStatus = null) => {
    return getInspectionValidityStyle(daysUntilExpiry, inspectionStatus);
  };

  // Background only version
  const getInspectionValidityBackgroundColor = (daysUntilExpiry, inspectionStatus = null) => {
    const style = getInspectionValidityStyle(daysUntilExpiry, inspectionStatus);
    return style.backgroundColor;
  };

  // Text color only version
  const getInspectionValidityTextColor = (daysUntilExpiry, inspectionStatus = null) => {
    const style = getInspectionValidityStyle(daysUntilExpiry, inspectionStatus);
    return style.color;
  };

  // Helper functions for manufacturer validity - returns style object
  // Only colors expired/warning items, valid items have no special styling
  const getManufacturerValidityStyle = (daysUntilExpiry) => {
    const settings = {
      expiredColor: user?.ppe_manufacturer_expired_color || defaultColors.expired,
      firstCheck: user?.ppe_manufacturer_first_check || 30,
      firstCheckColor: user?.ppe_manufacturer_first_check_color || defaultColors.firstCheck,
      secondCheck: user?.ppe_manufacturer_second_check || 60,
      secondCheckColor: user?.ppe_manufacturer_second_check_color || defaultColors.secondCheck
    };

    let bgColor = null;

    if (daysUntilExpiry < 0) {
      // Expired (negative days)
      bgColor = settings.expiredColor;
    } else if (daysUntilExpiry <= settings.firstCheck) {
      // Within first threshold
      bgColor = settings.firstCheckColor;
    } else if (daysUntilExpiry <= settings.secondCheck) {
      // Within second threshold
      bgColor = settings.secondCheckColor;
    }

    // Valid items (beyond second threshold) - no special styling
    if (!bgColor) {
      return {};
    }

    return {
      backgroundColor: bgColor,
      color: darkenColor(bgColor, 50)
    };
  };

  // Legacy function for backward compatibility - returns style object
  const getManufacturerValidityColor = (daysUntilExpiry) => {
    return getManufacturerValidityStyle(daysUntilExpiry);
  };

  // Background only version
  const getManufacturerValidityBackgroundColor = (daysUntilExpiry) => {
    const style = getManufacturerValidityStyle(daysUntilExpiry);
    return style.backgroundColor;
  };

  // Text color only version
  const getManufacturerValidityTextColor = (daysUntilExpiry) => {
    const style = getManufacturerValidityStyle(daysUntilExpiry);
    return style.color;
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('safety');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState([]);
  const [selectedModelFilter, setSelectedModelFilter] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState([]);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState([]);
  const [selectedManufacturerFilter, setSelectedManufacturerFilter] = useState([]);
  const [selectedValidityFilter, setSelectedValidityFilter] = useState([]);
  const [selectedManufacturerValidityFilter, setSelectedManufacturerValidityFilter] = useState([]);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState([]);
  const [selectedUserFilter, setSelectedUserFilter] = useState([]);
  const [selectedProjectFilter, setSelectedProjectFilter] = useState([]);
  const [cooperatingCompanies, setCooperatingCompanies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view', 'duplicate'
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);  // Default 100 to match backend
  const [totalItems, setTotalItems] = useState(0);  // Total items from backend
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    types: [],
    models: [],
    manufacturers: [],
    companies: [],
    groups: [],
    users: [],
    projects: []
  });
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [expandedTypes, setExpandedTypes] = useState(new Set());
  const [equipmentModels, setEquipmentModels] = useState([]);
  const [modelsLoaded, setModelsLoaded] = useState(false);  // Track if models are loaded
  const [showModelModal, setShowModelModal] = useState(false);
  const [selectedTypeForModel, setSelectedTypeForModel] = useState(null);
  const [modelModalMode, setModelModalMode] = useState('create');
  const [selectedModel, setSelectedModel] = useState(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [typeModalMode, setTypeModalMode] = useState('create');
  const [selectedType, setSelectedType] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'equipment', 'model', or 'group'
  const [deleteWarning, setDeleteWarning] = useState(null); // For cascade delete warnings
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [selectedEquipmentForInspection, setSelectedEquipmentForInspection] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedEquipmentForAssignment, setSelectedEquipmentForAssignment] = useState(null);
  const [currentAssignmentGroup, setCurrentAssignmentGroup] = useState(null);
  const [existingAssignments, setExistingAssignments] = useState({ hasUser: false, hasProject: false });
  const [showUnavailableEquipmentModal, setShowUnavailableEquipmentModal] = useState(false);
  const [unavailableEquipmentList, setUnavailableEquipmentList] = useState([]);

  // Return states
  const [showReturnConfirmation, setShowReturnConfirmation] = useState(false);
  const [equipmentToReturn, setEquipmentToReturn] = useState(null);
  const [returnType, setReturnType] = useState('both'); // 'user', 'project', or 'both'

  // Restore confirmation states
  const [showRestoreConfirmation, setShowRestoreConfirmation] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [restoreConfirmationData, setRestoreConfirmationData] = useState(null);

  // History modal states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedEquipmentForHistory, setSelectedEquipmentForHistory] = useState(null);
  
  // Equipment check modal states
  const [showEquipmentCheckModal, setShowEquipmentCheckModal] = useState(false);
  const [selectedEquipmentForCheck, setSelectedEquipmentForCheck] = useState(null);
  
  // Multi-equipment inspection states
  const [multiInspectionMode, setMultiInspectionMode] = useState(false);
  const [multiInspectionQueue, setMultiInspectionQueue] = useState([]);
  const [currentInspectionIndex, setCurrentInspectionIndex] = useState(0);
  
  // Inspection company assignment states
  const [pendingInspectionChanges, setPendingInspectionChanges] = useState(new Map());

  // Inspection price states for companies with can_inspection=true
  const [inspectionPrices, setInspectionPrices] = useState({}); // { typeId: { companyId: { price, currency } } }
  const [pendingPriceChanges, setPendingPriceChanges] = useState(new Map()); // Map<"typeId-companyId", { price, currency }>
  const [showCopyDropdown, setShowCopyDropdown] = useState(null); // companyId of column showing dropdown, or null

  // Inspection table data state
  const [inspectionTableData, setInspectionTableData] = useState([]);
  
  // Inspection notification modal state
  const [showInspectionNotificationModal, setShowInspectionNotificationModal] = useState(false);
  const [selectedEquipmentForNotification, setSelectedEquipmentForNotification] = useState(null);
  const [notificationCompanyId, setNotificationCompanyId] = useState(null);
  const [assignedUserForNotification, setAssignedUserForNotification] = useState(null);

  // Service modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedEquipmentForService, setSelectedEquipmentForService] = useState(null);
  const [serviceModalMode, setServiceModalMode] = useState('send'); // 'send' or 'return'
  const [serviceCompanies, setServiceCompanies] = useState([]);

  // Project assignment warning modal
  const [showProjectWarningModal, setShowProjectWarningModal] = useState(false);
  const [projectWarningEquipment, setProjectWarningEquipment] = useState(null);

  // Report modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const [equipmentData, setEquipmentData] = useState({
    safetyEquipment: [],
    safetyEquipmentTypes: [],
    globalTypes: [],
    equipmentAssignments: [],
    users: [],
    companies: [],
    projects: [],
    safetyEquipmentGroups: [],
    equipmentGroups: []
  });
  
  // Group management states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [preSelectedEquipment, setPreSelectedEquipment] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Import report modal
  const [showImportReportModal, setShowImportReportModal] = useState(false);
  const [importReportData, setImportReportData] = useState(null);

  // Ref for search input to maintain focus
  const searchInputRef = useRef(null);

  // Ref for aborting previous fetch requests (prevents race conditions)
  const abortControllerRef = useRef(null);

  // API Base URL
  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

  // Cleanup: abort pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch equipment data on mount and when company_can_inspection changes
  useEffect(() => {
    // Always fetch data on mount to initialize
    // If there are preset filters, they will trigger a second fetch via their useEffect
    fetchEquipmentData();

    // Always fetch these (they don't depend on filters)
    fetchFilterOptions();  // Fetch filter options for dropdowns (optimized - only used models/manufacturers)
    fetchCooperatingCompanies();
    fetchInspectionPrices();
  }, [user?.company_can_inspection]);

  // Refetch data when pagination, sorting, or filters change (but NOT on initial load)
  // Note: AbortController in fetchEquipmentData handles race conditions, so no need for isFilterChangeRef
  useEffect(() => {
    if (initialLoadDone) {
      console.log('🔄 Fetching data - Page:', currentPage, 'Per page:', itemsPerPage);
      fetchEquipmentData();
    }
  }, [currentPage, itemsPerPage, activeTab]);
  // Note: showDeletedOnly is handled by a separate useEffect below
  // Note: activeTab triggers refetch because Safety and Inspection tabs need different data (owner_only parameter)

  // Debounce search term (wait 3000ms after user stops typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Store whether search input had focus before update
      const hadFocus = document.activeElement === searchInputRef.current;

      setDebouncedSearchTerm(searchTerm);

      // Restore focus after state update if it had focus
      if (hadFocus) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }, 3000); // 3000ms delay (3 seconds) - encourages users to press Enter for immediate search

    return () => clearTimeout(timer); // Cleanup on each keystroke
  }, [searchTerm]);

  // Handle Enter key to immediately trigger search (bypass debounce)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setDebouncedSearchTerm(searchTerm); // Immediately update debounced term
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    searchInputRef.current?.focus();
  };

  // Refetch data when filters or sorting change (use debouncedSearchTerm instead of searchTerm)
  // Note: AbortController in fetchEquipmentData handles race conditions automatically
  useEffect(() => {
    console.log('🎬 Filter change useEffect triggered');
    console.log('   initialLoadDone:', initialLoadDone);
    console.log('   selectedValidityFilter:', selectedValidityFilter);
    console.log('   selectedManufacturerValidityFilter:', selectedManufacturerValidityFilter);

    if (initialLoadDone) {
      console.log('🔍 Filters or sorting changed - fetching data');

      // Save focus state before fetching
      const hadFocus = document.activeElement === searchInputRef.current;

      fetchEquipmentData().then(() => {
        // Restore focus after data loads if search had focus
        if (hadFocus && searchInputRef.current) {
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 0);
        }
      });
    } else {
      console.log('⏸️ Skipping fetch - initialLoadDone is false');
    }
  }, [debouncedSearchTerm, selectedStatusFilter, selectedTypeFilter, selectedModelFilter, selectedManufacturerFilter, selectedCompanyFilter, selectedValidityFilter, selectedManufacturerValidityFilter, selectedGroupFilter, selectedUserFilter, selectedProjectFilter, sortField, sortDirection]);

  // Refetch filter options when filters change (for cascading filters)
  useEffect(() => {
    if (initialLoadDone) {
      console.log('🎨 Filters changed - refreshing filter options for cascading');
      fetchFilterOptions();
    }
  }, [selectedTypeFilter, selectedManufacturerFilter, selectedModelFilter, selectedStatusFilter, selectedCompanyFilter, selectedGroupFilter]);

  // Maintain focus on search input even after data loads
  useEffect(() => {
    // If we were searching and data just loaded, restore focus
    if (!loading && searchTerm && searchInputRef.current) {
      const activeElement = document.activeElement;
      // Only restore focus if we're not focused on another input/button
      if (!activeElement || activeElement.tagName === 'BODY') {
        searchInputRef.current.focus();
      }
    }
  }, [loading, equipmentData.safetyEquipment]);

  // Close copy dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showCopyDropdown !== null) {
        setShowCopyDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showCopyDropdown]);

  // Refetch data when show_deleted option changes (not for other status filters)
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [previousShowDeleted, setPreviousShowDeleted] = useState(false);
  const [showDeletedOnly, setShowDeletedOnly] = useState(false);

  // Track show_deleted status separately to avoid unnecessary re-renders
  useEffect(() => {
    const currentShowDeleted = selectedStatusFilter.includes('show_deleted');
    if (currentShowDeleted !== showDeletedOnly) {
      setShowDeletedOnly(currentShowDeleted);
    }
  }, [selectedStatusFilter, showDeletedOnly]);

  useEffect(() => {
    if (initialLoadDone) {
      // Only refetch if show_deleted status actually changed
      if (showDeletedOnly !== previousShowDeleted) {
        fetchEquipmentData();
        setPreviousShowDeleted(showDeletedOnly);
      }
    }
  }, [showDeletedOnly, initialLoadDone, previousShowDeleted]);

  // Track if we have pending preset filters that need to be applied after initial load
  const [pendingPresetFilters, setPendingPresetFilters] = useState(null);

  // Handle preset filters and activeTab from navigation state (e.g., from Equipment overview cards)
  useEffect(() => {
    console.log('🎯 Preset filter useEffect triggered, location.state:', location.state);

    if (location.state?.presetFilter || location.state?.activeTab) {
      const { presetFilter, activeTab } = location.state;

      // Set active tab if provided
      if (activeTab) {
        console.log('📑 Setting active tab to:', activeTab);
        setActiveTab(activeTab);
      }

      // Store preset filters to be applied after initial load completes
      if (presetFilter) {
        console.log('🔧 Storing preset filters to apply after initial load:', presetFilter);
        setPendingPresetFilters(presetFilter);
      }

      // Clear the navigation state after storing filters
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Apply pending preset filters once initial load is done
  useEffect(() => {
    if (initialLoadDone && pendingPresetFilters) {
      console.log('✅ Initial load done! Applying pending preset filters:', pendingPresetFilters);

      if (pendingPresetFilters.validityFilter) {
        const filterValue = Array.isArray(pendingPresetFilters.validityFilter)
          ? pendingPresetFilters.validityFilter
          : [pendingPresetFilters.validityFilter];
        console.log('   Setting validityFilter to:', filterValue);
        setSelectedValidityFilter(filterValue);
        // Set appropriate sorting for inspection validity filter
        setSortField('Inspection');
        setSortDirection('asc');
      }

      if (pendingPresetFilters.manufacturerValidityFilter) {
        const filterValue = Array.isArray(pendingPresetFilters.manufacturerValidityFilter)
          ? pendingPresetFilters.manufacturerValidityFilter
          : [pendingPresetFilters.manufacturerValidityFilter];
        console.log('   Setting manufacturerValidityFilter to:', filterValue);
        setSelectedManufacturerValidityFilter(filterValue);
        // Set appropriate sorting for manufacturer validity filter
        setSortField('Validity');
        setSortDirection('asc');
      }

      if (pendingPresetFilters.userFilter) {
        const filterValue = Array.isArray(pendingPresetFilters.userFilter)
          ? pendingPresetFilters.userFilter
          : [pendingPresetFilters.userFilter];
        console.log('   Setting userFilter to:', filterValue);
        setSelectedUserFilter(filterValue);
        // Set appropriate sorting for user assignments
        setSortField('Assignment');
        setSortDirection('asc');
      }

      // Clear pending filters
      setPendingPresetFilters(null);
    }
  }, [initialLoadDone, pendingPresetFilters]);

  // Fetch inspection table after equipment data is loaded
  useEffect(() => {
    if (equipmentData.safetyEquipment.length > 0) {
      fetchInspectionTable();
    }
  }, [equipmentData.safetyEquipment]);


  // Log user data from UserContext
  useEffect(() => {
    console.log('=== USER CONTEXT DATA ===');
    console.log('Full user object:', user);
    console.log('User keys:', Object.keys(user || {}));
    console.log('user.company:', user?.company);
    console.log('user.company type:', typeof user?.company);
    console.log('user.company_name:', user?.company_name);
    console.log('user.id:', user?.id);
    console.log('========================');
  }, [user]);

  // Lazy-load equipment models when needed (for Equipment Types & Models tab and modals)
  useEffect(() => {
    // Load models when switching to Equipment Types & Models tab
    if (activeTab === 'types') {
      fetchEquipmentModels();
    }
    // Load models when opening equipment modal (needed for model selection)
    if (showModal) {
      fetchEquipmentModels();
    }
    // Load models when opening model modal
    if (showModelModal) {
      fetchEquipmentModels();
    }
  }, [activeTab, showModal, showModelModal]);


  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Build query params with current filters for cascading filter options
      const params = new URLSearchParams();

      // Add current filters so backend can return cascading options
      if (selectedTypeFilter.length > 0) {
        params.append('type', selectedTypeFilter.join(','));
      }

      if (selectedManufacturerFilter.length > 0) {
        params.append('manufacturer', selectedManufacturerFilter.join(','));
      }

      if (selectedModelFilter.length > 0) {
        params.append('model', selectedModelFilter.join(','));
      }

      if (selectedStatusFilter.length > 0) {
        const actualStatuses = selectedStatusFilter.filter(s => s !== 'show_deleted');
        if (actualStatuses.length > 0) {
          params.append('status', actualStatuses.join(','));
        }
      }

      if (selectedCompanyFilter.length > 0) {
        params.append('company', selectedCompanyFilter.join(','));
      }

      if (selectedGroupFilter.length > 0) {
        params.append('group', selectedGroupFilter.join(','));
      }

      const url = `${API_BASE_URL}/safetyequipment/filter-options/?${params.toString()}`;
      console.log('🔍 Fetching filter options from:', url);
      const response = await axios.get(url, config);
      console.log('📊 Filter options RAW response:', response);
      console.log('📊 Filter options data:', response.data);
      console.log('📊 Types:', response.data.types);
      console.log('📊 Models:', response.data.models);
      console.log('📊 Manufacturers:', response.data.manufacturers);
      console.log('📊 Companies:', response.data.companies);
      console.log('📊 Groups:', response.data.groups);
      console.log('📊 Users:', response.data.users);
      console.log('📊 Projects:', response.data.projects);
      setFilterOptions(response.data);
      console.log('✅ Filter options set to state');
    } catch (error) {
      console.error('❌ Error fetching filter options:', error);
      console.error('❌ Error response:', error.response);
    }
  };

  const fetchEquipmentData = async () => {
    // Cancel any previous request to prevent race conditions
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal,
      };

      // Build query params for server-side filtering and pagination
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', itemsPerPage);

      if (showDeletedOnly) {
        params.append('show_deleted', 'true');
      }

      // For Inspection Management tab (non-inspection companies), only show owned equipment
      if (activeTab === 'inspection' && !user?.company_can_inspection) {
        params.append('owner_only', 'true');
        params.append('inspection_tab', 'true');
      }

      // Add filters (use debounced search term)
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }

      // Filter out 'show_deleted' from status filter (it's handled separately above)
      const actualStatuses = selectedStatusFilter.filter(s => s !== 'show_deleted');
      if (actualStatuses.length > 0) {
        params.append('status', actualStatuses.join(','));
      }

      if (selectedTypeFilter.length > 0) {
        params.append('type', selectedTypeFilter.join(','));
      }

      if (selectedModelFilter.length > 0) {
        params.append('model', selectedModelFilter.join(','));
      }

      if (selectedManufacturerFilter.length > 0) {
        params.append('manufacturer', selectedManufacturerFilter.join(','));
      }

      if (selectedCompanyFilter.length > 0) {
        params.append('company', selectedCompanyFilter.join(','));
      }

      if (selectedGroupFilter.length > 0) {
        params.append('group', selectedGroupFilter.join(','));
      }

      if (selectedUserFilter.length > 0) {
        // Check if special "All Assignments" filter is selected
        if (selectedUserFilter.includes('__all_assignments__')) {
          params.append('user', '__all_assignments__');
        } else {
          params.append('user', selectedUserFilter.join(','));
        }
      }

      if (selectedProjectFilter.length > 0) {
        // Check if special filters are selected
        if (selectedProjectFilter.includes('__all_assignments__')) {
          params.append('project', '__all_assignments__');
        } else if (selectedProjectFilter.includes('__no_assignments__')) {
          params.append('project', '__no_assignments__');
        } else {
          params.append('project', selectedProjectFilter.join(','));
        }
      }

      if (selectedValidityFilter.length > 0) {
        console.log('📋 Adding validity_filter to params:', selectedValidityFilter);
        params.append('validity_filter', selectedValidityFilter.join(','));
      }

      if (selectedManufacturerValidityFilter.length > 0) {
        console.log('📋 Adding manufacturer_validity_filter to params:', selectedManufacturerValidityFilter);
        params.append('manufacturer_validity_filter', selectedManufacturerValidityFilter.join(','));
      }

      console.log('🌐 Final API URL params:', params.toString());

      // Add sorting parameter
      if (sortField) {
        console.log('🔄 Sorting - sortField:', sortField);
        console.log('🔄 Sorting - sortDirection:', sortDirection);

        // Map frontend column names to backend field names
        const fieldMap = {
          'Serial Number': 'serial_number',
          'Type': 'type',
          'Type / Brand': 'type',  // Sort by type name, then brand
          'Brand': 'brand',
          'Model': 'model',
          'Model / Serial Number': 'model',  // Sort by model name only
          'Status': 'status',
          'Company': 'company',
          'Inspection': 'inspection_validity',
          'Validity': 'manufacturer_validity',
          'Group': 'group',
          'Assignment': 'assigned_to',
        };

        const backendField = fieldMap[sortField] || sortField.toLowerCase().replace(/ /g, '_');
        const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;
        console.log('🔄 Backend field:', backendField);
        console.log('🔄 Ordering parameter:', ordering);
        params.append('ordering', ordering);
      }

      // Use optimized table-data endpoint with pagination and filters
      const tableDataUrl = `${API_BASE_URL}/safetyequipment/table-data/?${params.toString()}`;

      // Only fetch minimal additional data (lookup tables + company/project data for modals)
      // NOTE: equipmentModels are fetched separately (lazy-loaded) only when needed
      // NOTE: Using /users/minimal/ instead of /users/ for lighter payload (only id, name, email)
      const [tableDataResponse, typesResponse, globalTypesResponse, groupsResponse, serviceCompaniesResponse, usersResponse, companiesResponse, projectsResponse] = await Promise.all([
        axios.get(tableDataUrl, config),  // Optimized endpoint with pagination
        axios.get(`${API_BASE_URL}/safetyequipmenttype/?scope=company`, config),
        axios.get(`${API_BASE_URL}/safetyequipmenttype/?scope=all_global`, config),
        axios.get(`${API_BASE_URL}/safetyequipmentgroup/`, config),
        axios.get(`${API_BASE_URL}/servicecompany/`, config),
        axios.get(`${API_BASE_URL}/users/minimal/`, config),  // Lightweight user data for modals
        axios.get(`${API_BASE_URL}/company/`, config),  // Needed for company lookup
        axios.get(`${API_BASE_URL}/project/`, config)  // Needed for project lookup
      ]);

      // Fetch warehouse items that should be displayed in equipment management
      const warehouseItems = await fetchWarehouseItemsForEquipment();

      // Log structure of safety equipment data
      console.log('=== OPTIMIZED SAFETY EQUIPMENT DATA ===');
      console.log('Total equipment items:', tableDataResponse.data.count);
      console.log('Current page items:', tableDataResponse.data.results.length);
      console.log('First equipment item:', tableDataResponse.data.results[0]);
      console.log('=== END SAFETY EQUIPMENT DATA ===');

      // Convert warehouse items to equipment format
      const warehouseEquipmentItems = warehouseItems.map(item =>
        convertWarehouseItemToEquipment(item, user?.company?.name)
      );

      // Transform table-data format to match existing component expectations
      const transformedEquipment = tableDataResponse.data.results.map(item => ({
        ...item,
        // Map assignment data to match existing component structure
        ...(item.assignment && {
          // Keep the assignment data for backward compatibility with Assignment column logic
          _assignment_user_name: item.assignment.user_name,
          _assignment_project_name: item.assignment.project_name
        })
      }));

      console.log('📦 Transformed equipment count:', transformedEquipment.length);
      console.log('📦 First transformed item:', transformedEquipment[0]);

      setEquipmentData({
        safetyEquipment: [...transformedEquipment, ...warehouseEquipmentItems],
        safetyEquipmentTypes: typesResponse.data,
        globalTypes: globalTypesResponse.data,
        safetyEquipmentGroups: groupsResponse.data,
        equipmentGroups: groupsResponse.data,
        users: usersResponse.data,  // Minimal user data for modals (from /users/minimal/)
        companies: companiesResponse.data,  // For company lookup
        projects: projectsResponse.data,  // For project lookup
        // Note: equipmentAssignments removed (now included in equipment via assignment field)
      });

      // Set pagination info
      setTotalItems(tableDataResponse.data.count || 0);

      setServiceCompanies(serviceCompaniesResponse.data);
      // NOTE: equipmentModels are NOT set here - they're lazy-loaded when needed

    } catch (error) {
      // Ignore aborted requests (user changed page/filters before response arrived)
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        console.log('Request aborted - newer request in progress');
        return; // Don't update loading state - a new request is handling it
      }
      console.error('Error fetching equipment data:', error);
      setError(error.response?.data?.detail || 'Failed to fetch equipment data');
    }

    // Only update loading/initialLoadDone if this request wasn't aborted
    if (!controller.signal.aborted) {
      setLoading(false);
      if (!initialLoadDone) {
        setInitialLoadDone(true);
        setPreviousShowDeleted(showDeletedOnly);
      }
    }
  };

  // Lazy-load equipment models (only when needed for filters, modals, or Equipment Types & Models tab)
  const fetchEquipmentModels = async (force = false) => {
    console.log('📦 fetchEquipmentModels called - force:', force, 'modelsLoaded:', modelsLoaded);

    // Don't fetch if already loaded (unless force is true)
    if (modelsLoaded && !force) {
      console.log('⏭️ Skipping fetch - models already loaded and force=false');
      return;
    }

    try {
      console.log('🌐 Fetching equipment models from API...');
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      const response = await axios.get(`${API_BASE_URL}/safetyequipmentmodel/?scope=company`, config);
      console.log('✅ Received', response.data.length, 'equipment models from API');
      setEquipmentModels(response.data);
      setModelsLoaded(true);
    } catch (error) {
      console.error('Error fetching equipment models:', error);
      toast.error('Failed to load equipment models');
    }
  };

  // Fetch cooperating companies based on company type
  const fetchCooperatingCompanies = async () => {
    try {
      console.log('🔍 fetchCooperatingCompanies - user?.company_can_inspection:', user?.company_can_inspection);
      if (user?.company_can_inspection === true) {
        // For companies with can_inspection = true: get companies they cooperate with
        const response = await api.get('/cooperating-companies/manage/');
        console.log('🔍 cooperating-companies/manage response:', response.data);
        setCooperatingCompanies(response.data);
      } else if (user?.company_can_inspection === false) {
        // For companies with can_inspection = false: get companies that can perform inspections for them
        const response = await api.get('/cooperating-companies/available/');
        console.log('🔍 cooperating-companies/available response:', response.data);
        setCooperatingCompanies(response.data);
      } else {
        console.log('🔍 No company_can_inspection set, clearing cooperatingCompanies');
        setCooperatingCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching cooperating companies:', error);
      setCooperatingCompanies([]);
    }
  };

  // Fetch inspection prices for companies with can_inspection=true
  const fetchInspectionPrices = async () => {
    try {
      if (user?.company_can_inspection === true) {
        const response = await api.get('/inspection-prices/for-cooperating-companies/');

        // Transform response into price matrix: { typeId: { companyId: { price, currency } } }
        const priceMatrix = {};
        if (response.data.equipment_types) {
          response.data.equipment_types.forEach(typeData => {
            priceMatrix[typeData.id] = typeData.prices || {};
          });
        }

        setInspectionPrices(priceMatrix);
        console.log('Loaded inspection prices:', priceMatrix);
      }
    } catch (error) {
      console.error('Error fetching inspection prices:', error);
      toast.error('Failed to load inspection prices');
    }
  };

  // Fetch inspection table data and open modal if data exists
  const fetchInspectionTable = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      const response = await axios.get(`${API_BASE_URL}/inspectiontable/`, config);
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Data exists, process it and open inspection modal
        console.log('📋 Inspection table data received:', response.data);
        handleInspectionTableData(response.data);
      } else {
        // No data or empty response, do nothing
        console.log('📋 No inspection table data found');
      }
    } catch (error) {
      // If endpoint doesn't exist or returns error, just log it and continue
      console.log('📋 Inspection table endpoint not available or error:', error.message);
    }
  };

  // Handle inspection table data and open modal
  const handleInspectionTableData = (inspectionTableData) => {
    // Extract equipment IDs from the table array in the first item
    const equipmentIds = [];
    
    if (inspectionTableData.length > 0 && inspectionTableData[0].table) {
      inspectionTableData[0].table.forEach(tableItem => {
        if (tableItem.id) {
          equipmentIds.push(parseInt(tableItem.id));
        }
      });
    }
    
    console.log('📋 Extracted equipment IDs:', equipmentIds);
    
    // Find equipment items from our data
    const equipmentItems = equipmentData.safetyEquipment.filter(item => 
      equipmentIds.includes(item.id)
    );
    
    if (equipmentItems.length === 0) {
      console.log('📋 No matching equipment found for inspection table data');
      return;
    }
    
    // Set up multi-inspection mode with the found equipment
    setMultiInspectionMode(true);
    setMultiInspectionQueue(equipmentItems);
    setCurrentInspectionIndex(0);
    setSelectedEquipmentForInspection(equipmentItems[0]);
    
    // Store the inspection table data for reference
    setInspectionTableData(inspectionTableData);
    
    // Open the inspection modal
    setShowInspectionModal(true);
    
    console.log('📋 Opening inspection modal with equipment:', equipmentItems.map(item => item.id));
  };

  // Helper function to get current user company ID
  const getCurrentUserCompanyId = () => {
    // Get current user's company ID from UserContext
    if (user && user.company) {
      // If company is already a number (ID), return it directly
      if (typeof user.company === 'number') {
        return user.company;
      }
      
      // If company is a string (name), look up the ID
      if (typeof user.company === 'string' && equipmentData.companies && equipmentData.companies.length > 0) {
        const company = equipmentData.companies.find(c => c.name === user.company);
        return company ? company.id : null;
      }
    }
    
    return null;
  };

  // Helper function to check if user is the owner of the equipment
  const isOwner = (item) => {
    // Compare by company name (from optimized endpoint)
    if (item.company_name && user?.company_name) {
      return item.company_name === user.company_name;
    }
    // Fallback to ID comparison if available
    const currentUserCompanyId = getCurrentUserCompanyId();
    return item.company === currentUserCompanyId;
  };

  // Helper function to check if equipment is shared with current user's company
  const isSharedWithUs = (item) => {
    const currentUserCompanyId = getCurrentUserCompanyId();
    if (!currentUserCompanyId) {
      return false;
    }

    // Logic: Backend only sends us equipment if we have access via one of three ways:
    // 1. We own it (company = our company)
    // 2. We have inspection rights (inspection_company = our company)
    // 3. It's shared with us (is_shared = true AND shared_with_companies contains us)
    //
    // So we can deduce sharing by elimination:
    // - If we own it → not shared (we're the owner)
    // - If we have inspection rights → not shared (it's inspection-only)
    // - Otherwise → MUST be shared (only way backend would send it to us)

    // If we own it, it's not shared (we're the owner)
    if (item.company === currentUserCompanyId) {
      return false;
    }

    // If we have inspection rights, it's not shared (it's inspection-only)
    if (item.inspection_company === currentUserCompanyId) {
      return false;
    }

    // Backend sent us this equipment, but we're not the owner and don't have inspection rights,
    // so it MUST be shared with us (only remaining possibility)
    return true;
  };

  // Helper function to check if user can access equipment (owner OR shared with us)
  const canAccessEquipment = (item) => {
    return isOwner(item) || isSharedWithUs(item);
  };

  // Helper function to check if user has inspection rights for the equipment
  const hasInspectionRights = (item) => {
    // Check by company name (from optimized endpoint)
    if (item.inspection_company_name && user?.company_name) {
      return item.inspection_company_name === user.company_name;
    }
    // Fallback to ID comparison
    const currentUserCompanyId = getCurrentUserCompanyId();
    return item.inspection_company === currentUserCompanyId;
  };

  // Helper function to check if equipment is inspection-only (we can see it only because we're responsible for inspection)
  const isInspectionOnly = (item) => {
    // Equipment is inspection-only if:
    // - company is different than ours (not owner)
    // - NOT shared with us
    // - inspection_company equals our company (we're responsible for inspection)
    const isOwnerResult = isOwner(item);
    const isSharedResult = isSharedWithUs(item);
    const hasInspectionRightsResult = hasInspectionRights(item);

    return !isOwnerResult && !isSharedResult && hasInspectionRightsResult;
  };

  // Helper function to check if user can modify equipment status (owner OR shared, but NOT inspection-only)
  const canModifyEquipmentStatus = (item) => {
    return canAccessEquipment(item) && !isInspectionOnly(item);
  };

  // Helper function to check if user's company can do external inspections
  const userCanDoExternalInspections = () => {
    const currentUserCompanyId = getCurrentUserCompanyId();
    if (!currentUserCompanyId || !equipmentData.companies) {
      return false;
    }
    
    const userCompany = equipmentData.companies.find(c => c.id === currentUserCompanyId);
    const canInspect = userCompany?.can_inspection || false;
    
    console.log('DEBUG userCanDoExternalInspections:', {
      currentUserCompanyId,
      userCompany,
      can_inspection: userCompany?.can_inspection,
      result: canInspect
    });
    
    return canInspect;
  };

  // Helper function to count own company equipment (as displayed in table: groups + ungrouped items)
  const getOwnCompanyEquipmentCount = () => {
    const currentUserCompanyId = getCurrentUserCompanyId();
    if (!currentUserCompanyId) return 0;
    
    // Filter equipment for own company only
    const ownEquipment = equipmentData.safetyEquipment.filter(item => 
      item.company === currentUserCompanyId
    );
    
    // Count groups + ungrouped items (same logic as unified data structure)
    const groups = equipmentData.safetyEquipmentGroups?.filter(group => 
      group.company === currentUserCompanyId
    ) || [];
    
    const groupedItemIds = new Set();
    groups.forEach(group => {
      if (group.items && Array.isArray(group.items)) {
        group.items.forEach(itemId => groupedItemIds.add(itemId));
      }
    });
    
    const ungroupedItems = ownEquipment.filter(item => !groupedItemIds.has(item.id));
    
    return groups.length + ungroupedItems.length;
  };

  // Helper function to count other companies equipment (as displayed in table: all individual items)  
  const getOtherCompaniesEquipmentCount = () => {
    const currentUserCompanyId = getCurrentUserCompanyId();
    if (!currentUserCompanyId) return 0;
    
    // For external equipment, count all individual items (no grouping in display)
    const otherEquipmentCount = equipmentData.safetyEquipment.filter(item => 
      item.company !== currentUserCompanyId
    ).length;
    
    return otherEquipmentCount;
  };

  // Filter data based on search term and type filter
  const filterData = (data, searchFields) => {
    let filtered = data;
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        searchFields.some(field => {
          const fieldValue = item[field];
          
          // Handle regular fields
          if (fieldValue?.toString().toLowerCase().includes(searchTerm.toLowerCase())) {
            return true;
          }
          
          // Handle date fields - also search in formatted date
          if (field === 'safety_equipment_date_of_validity' && fieldValue) {
            const formattedDate = new Date(fieldValue).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
            return formattedDate.toLowerCase().includes(searchTerm.toLowerCase());
          }
          
          return false;
        })
      );
    }
    
    // NOTE: For safety and inspection tabs, all filtering (type, status, company, manufacturer, model, etc.)
    // is handled server-side by backend. No client-side filtering needed.
    // This filterData function is mainly used for types tab and search-only filtering.
    
    return filtered;
  };

  // Get models for a specific equipment type (used in types tab)
  const getModelsForType = (typeId) => {
    // typeId is the company type ID
    // Find the company type to get its global_type_id
    const companyType = equipmentData.safetyEquipmentTypes?.find(type => type.id === typeId);

    if (!companyType || !companyType.global_type_id) {
      return [];
    }

    // Models are now linked to GLOBAL types, not company types
    // So we need to find models where:
    // 1. safety_equipment_type === global_type_id (models are linked to global type)
    // 2. company === user's company (only models from this company)
    return equipmentModels.filter(model =>
      model.safety_equipment_type === companyType.global_type_id &&
      model.company === companyType.company
    );
  };

  // Get available models for filter dropdown based on currently displayed equipment
  const getAvailableModelsForFilter = () => {
    // Use filter options from backend (only models actually used in equipment)
    console.log('🔧 getAvailableModelsForFilter called, filterOptions.models:', filterOptions.models);
    return filterOptions.models || [];
  };

  // Helper function to check if equipment item matches selected type filter (by global_type_id)
  const itemMatchesTypeFilter = (item) => {
    if (selectedTypeFilter.length === 0) return true;

    // Use the global_type_id directly from the serialized equipment
    if (!item.global_type_id) return false;

    return selectedTypeFilter.includes(item.global_type_id);
  };

  // Get unique global types from all equipment items
  const getUniqueGlobalTypesForFilter = () => {
    const allEquipment = equipmentData.safetyEquipment || [];
    const globalTypes = equipmentData.globalTypes || [];

    // Count equipment for each global type using the serialized global_type_id
    const typeCounts = new Map();

    allEquipment.forEach(item => {
      if (item.global_type_id) {
        typeCounts.set(item.global_type_id, (typeCounts.get(item.global_type_id) || 0) + 1);
      }
    });

    // Filter global types to only those with equipment count > 0
    const typesWithEquipment = globalTypes
      .filter(type => (typeCounts.get(type.id) || 0) > 0)
      .map(type => ({
        id: type.id,
        name: type.name,
        count: typeCounts.get(type.id) || 0
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return typesWithEquipment;
  };

  // Get available companies for filter dropdown (only companies from current data)
  const getAvailableCompaniesForFilter = () => {
    // Use complete filter options from backend instead of current page data
    console.log('🔧 getAvailableCompaniesForFilter called, equipmentData.companies:', equipmentData.companies);
    return equipmentData.companies || [];
  };

  // Get available manufacturers for filter dropdown (only manufacturers from current data)
  const getAvailableManufacturersForFilter = () => {
    // Use filter options from backend (only manufacturers actually used in equipment)
    console.log('🔧 getAvailableManufacturersForFilter called, filterOptions.manufacturers:', filterOptions.manufacturers);
    return filterOptions.manufacturers || [];
  };

  // Get brand from equipment model (helper function)
  const getBrandFromModel = (item) => {
    // Use the brand from the serializer (already resolved by backend)
    if (item.safety_equipment_model_brand) {
      return item.safety_equipment_model_brand;
    }
    // Fallback to legacy field if no model is selected
    return item.safety_equipment_brand || '-';
  };

  // Get model name from equipment model (helper function)
  const getModelNameFromModel = (item) => {
    // Use the model name from the serializer (already resolved by backend)
    return item.safety_equipment_model_name || '-';
  };

  // Get manufacturer from equipment model (helper function)
  const getManufacturerFromModel = (item) => {
    // Use the manufacturer from the serializer (already resolved by backend)
    if (item.safety_equipment_model_manufacturer) {
      return item.safety_equipment_model_manufacturer;
    }
    return '-';
  };

  // Format days status for validity date (helper function)
  const formatDaysStatus = (validityDate) => {
    if (!validityDate) return null;
    
    const today = new Date();
    const expiry = new Date(validityDate);
    const timeDiff = expiry.getTime() - today.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    
    if (daysDiff > 0) return `Expires in ${daysDiff} days`;
    if (daysDiff < 0) return `Expired ${Math.abs(daysDiff)} days ago`;
    return 'Expires today';
  };

  // Calculate manufacturer validity date and status
  const getManufacturerValidityStatus = (item) => {
    // Use cached manufacturer_validity_date from backend
    const validityDate = item.manufacturer_validity_date;

    // NULL means model has no time_to_manufacturer_inspection set
    if (!validityDate) {
      return {
        hasData: false,
        validityDate: null,
        daysUntilExpiry: null,
        status: 'no_data'
      };
    }

    // Parse validity date from backend
    const validityDateTime = new Date(validityDate);
    const today = new Date();
    const daysUntilExpiry = Math.floor((validityDateTime - today) / (1000 * 60 * 60 * 24));

    // Check if this is "lifetime" validity (>50 years in the future)
    // 50 years * 365 days = 18,250 days
    if (daysUntilExpiry > 18250) {
      return {
        hasData: true,
        validityDate: validityDateTime,
        daysUntilExpiry,
        status: 'lifetime',
        isLifetime: true
      };
    }

    // Normal validity date
    return {
      hasData: true,
      validityDate: validityDateTime,
      daysUntilExpiry,
      status: daysUntilExpiry < 0 ? 'expired' :
              daysUntilExpiry <= (user?.ppe_manufacturer_first_check || 30) ? 'expires_soon' :
              daysUntilExpiry <= (user?.ppe_manufacturer_second_check || 60) ? 'expires_medium' :
              'valid'
    };
  };

  // Format manufacturer validity days status
  const formatManufacturerDaysStatus = (validityDate) => {
    if (!validityDate) return null;
    
    const today = new Date();
    const expiry = new Date(validityDate);
    const timeDiff = expiry.getTime() - today.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    
    if (daysDiff > 0) return `Expires in ${daysDiff} days`;
    if (daysDiff < 0) return `Expired ${Math.abs(daysDiff)} days ago`;
    return 'Expires today';
  };

  // Special filter for safety equipment that includes brand from model
  const filterSafetyEquipment = (equipment) => {
    // Ensure equipment is an array
    if (!equipment || !Array.isArray(equipment)) {
      console.warn('filterSafetyEquipment: equipment is not an array:', equipment);
      return [];
    }

    // NOTE: Server-side search is now enabled for safety and inspection tabs
    // Backend handles all search filtering, so we don't need client-side filtering
    // Just return the equipment as-is (already filtered by backend)
    return equipment;
  };

  // Special filter for equipment types that includes models data
  const filterEquipmentTypes = (types) => {
    if (!searchTerm) {
      return types;
    }

    return types.filter(type => {
      const searchLower = searchTerm.toLowerCase();
      
      // Search in type name
      if (type.name?.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in type comment
      if (type.comment?.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in associated models (name, brand, manufacturer)
      const typeModels = getModelsForType(type.id);
      const modelMatch = typeModels.some(model => {
        return (
          model.name?.toLowerCase().includes(searchLower) ||
          model.brand?.toLowerCase().includes(searchLower) ||
          model.manufacturer?.toLowerCase().includes(searchLower) ||
          model.description?.toLowerCase().includes(searchLower)
        );
      });
      
      return modelMatch;
    });
  };

  // Get filtered and sorted data for current tab
  const getCurrentData = () => {
    let data = [];
    switch (activeTab) {
      case 'safety': {
        // Data is already filtered by backend (server-side filtering)
        // Backend handles ALL filters including search, so no client-side filtering needed
        let filteredEquipment = filterSafetyEquipment(equipmentData.safetyEquipment);

        // Note: ALL filters (Type, Status, Model, Company, Manufacturer, Validity, Search, Group) are now handled server-side
        // No client-side filtering needed for safety tab - data comes pre-filtered from backend

        data = filteredEquipment;
        break;
      }
      case 'inspection': {
        const canInspection = user?.company_can_inspection || false;

        if (canInspection) {
          // For companies with can_inspection=true: show equipment types (company-specific)
          let filteredTypes = equipmentData.safetyEquipmentTypes || [];

          // Filter only company-specific types (those with global_type_id)
          filteredTypes = filteredTypes.filter(type => type.global_type_id != null);

          // Apply search filter
          if (searchTerm) {
            filteredTypes = filteredTypes.filter(type =>
              type.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              type.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }

          data = filteredTypes;
        } else {
          // For companies with can_inspection=false: show equipment items
          // NOTE: ALL filtering (including search) is now done server-side
          // Just use the data from backend directly
          let filteredInspectionEquipment = filterSafetyEquipment(equipmentData.safetyEquipment);

          // Note: Type, Model, Manufacturer, Company, and Search filters are handled server-side
          // No client-side filtering needed for inspection tab

          data = filteredInspectionEquipment;
        }
        break;
      }
      case 'groups': {
        // Show equipment groups
        let filteredGroups = equipmentData.safetyEquipmentGroups || [];

        // Apply search filter
        if (searchTerm) {
          filteredGroups = filteredGroups.filter(group =>
            group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            group.description?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        data = filteredGroups;
        break;
      }
      case 'types':
        // Use special filtering for types that includes models data
        data = filterEquipmentTypes(equipmentData.safetyEquipmentTypes);
        break;
      default:
        data = [];
    }

    // Apply sorting if sortField is set
    // NOTE: Most sorting is now done server-side. Client-side sorting only for fields not handled by backend
    if (sortField && activeTab !== 'safety') {
      // Only apply client-side sorting for non-safety tabs (types, models)
      data = [...data].sort((a, b) => {
        let aValue = '';
        let bValue = '';

        // Map sort field to actual data property
        switch (sortField) {
          case 'Type / Brand':
            // Sort by type name (which represents the global type name)
            aValue = a.safety_equipment_type_name || '';
            bValue = b.safety_equipment_type_name || '';
            break;
          case 'Company':
            // Primary sort by owner company name
            aValue = a.company_name || '';
            bValue = b.company_name || '';

            // If owner company names are the same, sort by inspection company name
            if (aValue === bValue) {
              const aInspectionValue = a.inspection_company_name || '';
              const bInspectionValue = b.inspection_company_name || '';
              return sortDirection === 'asc'
                ? aInspectionValue.localeCompare(bInspectionValue)
                : bInspectionValue.localeCompare(aInspectionValue);
            }
            break;
          case 'Brand':
            aValue = getBrandFromModel(a);
            bValue = getBrandFromModel(b);
            break;
          case 'Model':
            aValue = getModelNameFromModel(a);
            bValue = getModelNameFromModel(b);
            break;
          case 'Manufacturer':
            aValue = getManufacturerFromModel(a);
            bValue = getManufacturerFromModel(b);
            break;
          case 'Serial Number':
            aValue = a.safety_equipment_serial_number || '';
            bValue = b.safety_equipment_serial_number || '';
            break;
          case 'Validity':
            // Handle items without manufacturer validity data
            const aManufacturerValidity = getManufacturerValidityStatus(a);
            const bManufacturerValidity = getManufacturerValidityStatus(b);

            if (!aManufacturerValidity.hasData && !bManufacturerValidity.hasData) return 0;
            if (!aManufacturerValidity.hasData) return sortDirection === 'asc' ? 1 : 1;
            if (!bManufacturerValidity.hasData) return sortDirection === 'asc' ? -1 : -1;

            // Both have manufacturer validity data - sort by date
            aValue = aManufacturerValidity.validityDate;
            bValue = bManufacturerValidity.validityDate;
            break;
          case 'Inspection':
            // Handle items without inspection - always put them at the end
            const aHasInspection = a.latest_inspection?.validity_date;
            const bHasInspection = b.latest_inspection?.validity_date;
            
            if (!aHasInspection && !bHasInspection) return 0; // Both no inspection - equal
            if (!aHasInspection) return sortDirection === 'asc' ? 1 : 1; // a has no inspection - put at end regardless of sort direction
            if (!bHasInspection) return sortDirection === 'asc' ? -1 : -1; // b has no inspection - put at end regardless of sort direction
            
            // Both have inspections - sort by date only
            aValue = new Date(a.latest_inspection.validity_date);
            bValue = new Date(b.latest_inspection.validity_date);
            break;
          case 'Assignment':
            const aAssignment = equipmentData?.equipmentAssignments?.find(assignment => 
              assignment.safety_equipment === a.id && assignment.status === 'assigned'
            );
            const bAssignment = equipmentData?.equipmentAssignments?.find(assignment => 
              assignment.safety_equipment === b.id && assignment.status === 'assigned'
            );
            
            const aUser = aAssignment ? equipmentData?.users?.find(u => u.id === aAssignment.user) : null;
            const bUser = bAssignment ? equipmentData?.users?.find(u => u.id === bAssignment.user) : null;
            
            aValue = aUser 
              ? (aUser.first_name && aUser.last_name ? `${aUser.first_name} ${aUser.last_name}` : aUser.username || '')
              : '';
            bValue = bUser 
              ? (bUser.first_name && bUser.last_name ? `${bUser.first_name} ${bUser.last_name}` : bUser.username || '')
              : '';
            break;
          case 'Group':
            // Find the group this equipment belongs to and sort by group name
            const aGroup = equipmentData?.safetyEquipmentGroups?.find(g => 
              g.equipment_items?.some(groupItem => groupItem.id === a.id)
            );
            const bGroup = equipmentData?.safetyEquipmentGroups?.find(g => 
              g.equipment_items?.some(groupItem => groupItem.id === b.id)
            );
            
            aValue = aGroup?.name || '';
            bValue = bGroup?.name || '';
            break;
          case 'Status':
            aValue = a.safety_equipment_status || '';
            bValue = b.safety_equipment_status || '';
            break;
          case 'Name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'Comment':
            aValue = a.comment || '';
            bValue = b.comment || '';
            break;
          default:
            return 0;
        }

        // Handle date comparison for Validity and Inspection
        if (sortField === 'Validity' || sortField === 'Inspection') {
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }

        // Convert to string and compare for other fields
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return data;
  };

  // Pagination logic
  const filteredData = getCurrentData();

  // For inspection tab with can_inspection=true, we display types not items
  // So we need to use the count of types for pagination, not totalItems (which is count of items)
  const canInspection = user?.company_can_inspection || false;
  const displayingTypes = activeTab === 'inspection' && canInspection;

  const effectiveTotalItems = displayingTypes
    ? filteredData.length  // Use filtered data length (includes search filter)
    : totalItems;

  // Server-side pagination - no client-side slicing needed (except for types view which is client-side)
  const totalPages = Math.ceil(effectiveTotalItems / itemsPerPage) || 1;

  // For types view (inspection with can_inspection=true), apply client-side pagination
  // For other views, data is already paginated from server
  const paginatedData = displayingTypes
    ? filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : filteredData;

  // Get visible page numbers for pagination
  const getVisiblePages = () => {
    const maxVisiblePages = 5;
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Reset to first page ONLY when filters change (not when currentPage changes)
  // Use debouncedSearchTerm instead of searchTerm to avoid resetting page on every keystroke
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, debouncedSearchTerm, selectedTypeFilter, selectedModelFilter, showDeletedOnly, selectedCompanyFilter, selectedManufacturerFilter, selectedValidityFilter, selectedManufacturerValidityFilter, selectedGroupFilter, selectedUserFilter, selectedProjectFilter, itemsPerPage]);

  // Set default sorting on component mount and tab changes
  useEffect(() => {
    // Set default sorting to 'Inspection' for safety tab
    if (activeTab === 'safety') {
      setSortField('Inspection');
      setSortDirection('asc');
    } else {
      setSortField('');
      setSortDirection('asc');
    }
  }, [activeTab]);  // Removed equipmentData - only reset sorting when tab changes, not on every data update

  // Reset model filter when type filter changes
  useEffect(() => {
    setSelectedModelFilter([]);
  }, [selectedTypeFilter]);

  // Clear selections when tab changes or data changes
  useEffect(() => {
    setSelectedItems(new Set());
    setPendingInspectionChanges(new Map()); // Clear pending changes when filters change
  }, [activeTab, searchTerm, selectedTypeFilter, selectedModelFilter, showDeletedOnly, selectedCompanyFilter, selectedManufacturerFilter, selectedValidityFilter, selectedManufacturerValidityFilter, selectedGroupFilter, selectedUserFilter, selectedProjectFilter]);

  // Handler for double click on equipment row
  const handleEquipmentDoubleClick = (item) => {
    setSelectedEquipmentForCheck(item.id);
    setShowEquipmentCheckModal(true);
  };

  // Modal handlers
  const openModal = (mode, item = null) => {
    setModalMode(mode);
    // For edit/view/duplicate modes, pass only the ID to improve performance
    // Modal will fetch full details when needed
    // For create mode, pass null
    if ((mode === 'edit' || mode === 'view' || mode === 'duplicate') && item && item.id) {
      setSelectedItem(item.id);
    } else {
      setSelectedItem(item); // null for create mode
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setModalMode('create');
  };

  // Handle status change
  const handleStatusChange = async (equipmentId, newStatus) => {
    // If changing to service, check for project assignment first
    if (newStatus === 'service') {
      let equipmentToCheck = null;

      // Check if there are multiple selected items and this item is one of them
      if (selectedItems.size > 0 && selectedItems.has(equipmentId)) {
        // Get all selected equipment items
        const selectedEquipmentList = equipmentData.safetyEquipment.filter(item => selectedItems.has(item.id));

        // Check if ANY of the selected items has a project assignment
        const itemWithProject = selectedEquipmentList.find(item => item.assignment?.project_name);

        if (itemWithProject) {
          // Show warning for the first item with project assignment
          setProjectWarningEquipment(selectedEquipmentList);
          setShowProjectWarningModal(true);
          return;
        }

        setSelectedEquipmentForService(selectedEquipmentList);
      } else {
        // Single equipment selection
        const equipment = equipmentData.safetyEquipment.find(item => item.id === equipmentId);

        // Check if this equipment has a project assignment
        if (equipment?.assignment?.project_name) {
          setProjectWarningEquipment([equipment]);
          setShowProjectWarningModal(true);
          return;
        }

        setSelectedEquipmentForService(equipment);
      }

      setServiceModalMode('send'); // Normal mode: sending to service
      setShowServiceModal(true);
      return;
    }

    // If changing to issued, show assignment modal
    if (newStatus === 'issued') {
      const equipment = equipmentData.safetyEquipment.find(item => item.id === equipmentId);
      openAssignmentModal(equipment);
      return;
    }

    // If changing from issued/received/returned to available, show return modal (same as "Mark as Returned" button)
    if (newStatus === 'available') {
      const equipment = equipmentData.safetyEquipment.find(item => item.id === equipmentId);
      if (equipment && ['issued', 'received', 'returned'].includes(equipment.safety_equipment_status)) {
        handleReturn(equipment);
        return;
      }
      // If changing from service to available, show service modal in return mode
      if (equipment && equipment.safety_equipment_status === 'service') {
        // Check if there are multiple selected items and this item is one of them
        if (selectedItems.size > 0 && selectedItems.has(equipmentId)) {
          // Get all selected equipment items that have 'service' status
          const selectedServiceEquipment = equipmentData.safetyEquipment.filter(item =>
            selectedItems.has(item.id) && item.safety_equipment_status === 'service'
          );
          setSelectedEquipmentForService(selectedServiceEquipment);
        } else {
          // Single equipment selection
          setSelectedEquipmentForService(equipment);
        }
        setServiceModalMode('return');
        setShowServiceModal(true);
        return;
      }
    }

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      await axios.patch(`${API_BASE_URL}/safetyequipment/${equipmentId}/`, {
        safety_equipment_status: newStatus
      }, config);

      // Update local data
      setEquipmentData(prevData => ({
        ...prevData,
        safetyEquipment: prevData.safetyEquipment.map(item =>
          item.id === equipmentId
            ? { 
                ...item, 
                safety_equipment_status: newStatus,
                // Clear service data if changing from service status
                ...(item.safety_equipment_status === 'service' && newStatus !== 'service' ? {
                  service_company_id: null,
                  service_company_name: null,
                  service_date: null,
                  service_notes: null
                } : {})
              }
            : item
        )
      }));

      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
      // Refresh data to revert to original state
      await fetchEquipmentData();
    }
  };

  // Handle save from modal
  const handleSave = async (savedItem) => {
    // AssignmentModal now correctly waits for ALL HTTP requests using Promise.all()
    // before calling onSave, so we can refresh immediately
    await fetchEquipmentData(); // Refresh data after save

    // Force refresh models (for manufacturers and model dropdowns)
    await fetchEquipmentModels(true);

    // Refresh filter options (for manufacturers and model dropdowns)
    await fetchFilterOptions();
  };

  // Handle inspection company change (local only - not saved until Save button)
  const handleInspectionCompanyChange = (equipmentId, companyId) => {
    const newChanges = new Map(pendingInspectionChanges);

    // If there are selected items, apply the change to all selected items
    if (selectedItems.size > 0 && selectedItems.has(equipmentId)) {
      // Apply to all selected items
      selectedItems.forEach(itemId => {
        if (companyId) {
          newChanges.set(itemId, companyId);
        } else {
          newChanges.set(itemId, null);
        }
      });
    } else {
      // Apply to single item
      if (companyId) {
        newChanges.set(equipmentId, companyId);
      } else {
        newChanges.set(equipmentId, null);
      }
    }

    setPendingInspectionChanges(newChanges);
  };

  // Handle inspection price change for type/company combination
  const handlePriceChange = (typeId, globalTypeId, companyId, priceValue) => {
    const priceKey = `${typeId}-${companyId}`;
    const newPendingChanges = new Map(pendingPriceChanges);

    if (priceValue === '' || priceValue === null || priceValue === undefined) {
      // Remove from pending if empty
      newPendingChanges.delete(priceKey);
    } else {
      // Add or update pending change
      newPendingChanges.set(priceKey, {
        typeId,
        globalTypeId,
        companyId,
        price: priceValue,
        currency: 'PLN'
      });
    }

    setPendingPriceChanges(newPendingChanges);
  };

  // Copy prices from one company to another
  const copyPricesFromCompany = (sourceCompanyId, targetCompanyId) => {
    // Get all equipment types
    const equipmentTypes = equipmentData.safetyEquipmentTypes || [];
    const typesWithGlobalId = equipmentTypes.filter(type => type.global_type_id != null);

    let copiedCount = 0;
    const newPendingChanges = new Map(pendingPriceChanges);

    typesWithGlobalId.forEach(type => {
      // Get price from source company
      const sourcePrice = inspectionPrices[type.id]?.[sourceCompanyId];

      if (sourcePrice && sourcePrice.price) {
        const priceKey = `${type.id}-${targetCompanyId}`;

        // Add to pending changes
        newPendingChanges.set(priceKey, {
          typeId: type.id,
          globalTypeId: type.global_type_id,
          companyId: targetCompanyId,
          price: sourcePrice.price,
          currency: sourcePrice.currency || 'PLN'
        });

        copiedCount++;
      }
    });

    setPendingPriceChanges(newPendingChanges);
    setShowCopyDropdown(null); // Close dropdown

    if (copiedCount > 0) {
      toast.success(`Copied ${copiedCount} price(s). Click Save to apply.`);
    } else {
      toast.info('No prices to copy from selected company');
    }
  };

  // Save inspection prices to backend
  const saveInspectionPrices = async () => {
    if (pendingPriceChanges.size === 0) {
      toast.info('No price changes to save');
      return;
    }

    try {
      // Transform pending changes into API format
      const prices = Array.from(pendingPriceChanges.values()).map(change => ({
        client_company: change.companyId,
        global_type_id: change.globalTypeId,
        price: change.price,
        currency: change.currency
      }));

      // Send to backend using bulk-upsert endpoint
      const response = await api.post('/inspection-prices/bulk-upsert/', {
        prices: prices
      });

      console.log('Save prices response:', response.data);

      // Show success message
      toast.success(`Saved ${response.data.created + response.data.updated} price(s)`);

      // Show errors if any
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Price save errors:', response.data.errors);
        response.data.errors.forEach(error => {
          toast.error(`Error: ${error.error}`);
        });
      }

      // Clear pending changes
      setPendingPriceChanges(new Map());

      // Refresh prices from backend
      await fetchInspectionPrices();

    } catch (error) {
      console.error('Error saving inspection prices:', error);
      toast.error(error.response?.data?.error || 'Failed to save inspection prices');
    }
  };

  // Handle bulk inspection company assignment and save all pending changes
  const handleBulkInspectionCompanyAssignment = async () => {
    // Only save pending changes (no bulk assignment from separate select)
    const allChanges = new Map(pendingInspectionChanges);

    if (allChanges.size === 0) {
      setError('No changes to save');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // OPTIMIZED: Use bulk update endpoint (one request instead of many)
      const updates = Array.from(allChanges.entries()).map(([itemId, companyId]) => ({
        equipment_id: parseInt(itemId),
        inspection_company: companyId ? parseInt(companyId) : null
      }));

      const response = await axios.post(
        `${API_BASE_URL}/safetyequipment/bulk-update-inspection-company/`,
        { updates },
        config
      );

      if (response.data.success) {
        // Show success message with details
        const { updated_count, failed_count } = response.data;
        if (failed_count > 0) {
          toast.warning(`Updated ${updated_count} items. ${failed_count} failed.`);
          console.warn('Failed updates:', response.data.failed_updates);
        } else {
          toast.success(`Successfully updated ${updated_count} equipment items`);
        }

        // Clear selections and reset
        setSelectedItems(new Set());
        setPendingInspectionChanges(new Map());

        // Refresh data from server to get updated information
        await fetchEquipmentData();
      } else {
        throw new Error(response.data.error || 'Bulk update failed');
      }

    } catch (error) {
      console.error('Error assigning inspection company:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to assign inspection company');
    }
  };

  // Handle individual item selection
  const handleItemSelect = (itemId) => {
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(itemId)) {
      newSelectedItems.delete(itemId);
    } else {
      newSelectedItems.add(itemId);
    }
    setSelectedItems(newSelectedItems);
  };

  // Handle select all items (works on all filtered data, not just current page)
  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all filtered items (not just current page)
      const allFilteredItems = filteredData.map(item => item.id);
      const newSelectedItems = new Set([...selectedItems, ...allFilteredItems]);
      setSelectedItems(newSelectedItems);
    } else {
      // Deselect all filtered items
      const filteredItemIds = new Set(filteredData.map(item => item.id));
      const newSelectedItems = new Set([...selectedItems].filter(id => !filteredItemIds.has(id)));
      setSelectedItems(newSelectedItems);
    }
  };

  // Check if all filtered items are selected
  const isAllSelected = () => {
    if (filteredData.length === 0) return false;
    return filteredData.every(item => selectedItems.has(item.id));
  };

  // Check if some filtered items are selected (for indeterminate state)
  const isSomeSelected = () => {
    return filteredData.some(item => selectedItems.has(item.id)) && !isAllSelected();
  };

  // Get count of selected items from current filtered data plus pending changes
  const getSelectedCount = () => {
    const currentFilteredIds = new Set(filteredData.map(item => item.id));
    const selectedInCurrentTab = Array.from(selectedItems).filter(id => currentFilteredIds.has(id)).length;
    const pendingChangesInCurrentTab = Array.from(pendingInspectionChanges.keys()).filter(id => currentFilteredIds.has(id)).length;
    return selectedInCurrentTab + pendingChangesInCurrentTab;
  };

  // Handle equipment type row expansion (accordion-style - only one expanded at a time)
  const toggleTypeExpansion = (typeId) => {
    if (expandedTypes.has(typeId)) {
      // If this type is already expanded, collapse it
      setExpandedTypes(new Set());
    } else {
      // If this type is not expanded, expand only this one (close all others)
      setExpandedTypes(new Set([typeId]));
    }
  };

  // Handle model modal
  const openModelModal = (mode, typeId, model = null) => {
    console.log('DEBUG: openModelModal called with:', { mode, typeId, model });
    setModelModalMode(mode);
    setSelectedTypeForModel(typeId);
    setSelectedModel(model);
    setShowModelModal(true);
    
    // Additional debug after state setting
    setTimeout(() => {
      console.log('DEBUG: State after openModelModal:', {
        modelModalMode: mode,
        selectedTypeForModel: typeId,
        selectedModel: model
      });
    }, 100);
  };

  const closeModelModal = () => {
    setShowModelModal(false);
    setSelectedTypeForModel(null);
    setSelectedModel(null);
    setModelModalMode('create');
  };

  // Type modal handlers
  const openTypeModal = (mode, type = null) => {
    setTypeModalMode(mode);
    setSelectedType(type);
    setShowTypeModal(true);
  };

  const closeTypeModal = () => {
    setShowTypeModal(false);
    setSelectedType(null);
    setTypeModalMode('create');
  };

  // Handle type save
  const handleTypeSave = async () => {
    // Refresh types
    await fetchEquipmentData();

    // Force refresh models
    await fetchEquipmentModels(true);
  };

  // Handle Excel import
  const handleImportEquipment = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.onchange = handleFileSelect;
    input.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API_BASE_URL}/safetyequipment/import-excel/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Show detailed import report modal instead of toast
        setImportReportData(response.data);
        setShowImportReportModal(true);
        // Note: Data refresh happens when user closes the import report modal
      } else {
        toast.error(response.data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing file:', error);
      toast.error('Failed to import file. Please check the format and try again.');
    }
  };

  // Handle closing import report modal and refresh data
  const handleCloseImportReport = async () => {
    setShowImportReportModal(false);

    // Refresh all data after import
    await fetchEquipmentData();

    // Force refresh models and filter options
    await fetchEquipmentModels(true);
    await fetchFilterOptions();
  };

  // Handle Excel export
  const handleExportEquipment = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/safetyequipment/export-excel/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `safety_equipment_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Equipment data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data. Please try again.');
    }
  };

  // Handle group modal
  const openGroupModal = (mode, group = null, preSelectedEquipment = []) => {
    setGroupModalMode(mode);
    setSelectedGroup(group);
    setPreSelectedEquipment(preSelectedEquipment);
    setShowGroupModal(true);
  };

  const closeGroupModal = () => {
    setShowGroupModal(false);
    setSelectedGroup(null);
    setPreSelectedEquipment([]);
    setGroupModalMode('create');
  };

  const handleGroupSave = (savedGroup) => {
    // Clear selected items if we just created a group with pre-selected equipment
    if (preSelectedEquipment.length > 0) {
      setSelectedItems(new Set());
    }
    
    // Refresh equipment data to get updated groups
    fetchEquipmentData();
  };

  // Handle Create Group button click with validation
  const handleCreateGroupClick = () => {
    // Get selected equipment items (only individual items, not groups)
    const selectedEquipmentItems = Array.from(selectedItems)
      .map(id => equipmentData.safetyEquipment.find(item => item.id === id))
      .filter(item => item && !item.isGroup);
    
    // Validate equipment statuses only if items are selected
    if (selectedEquipmentItems.length > 0) {
      const statuses = selectedEquipmentItems.map(item => item.safety_equipment_status);
      const uniqueStatuses = [...new Set(statuses)];
      
      // Check if all items have the same status
      if (uniqueStatuses.length > 1) {
        toast.error("Cannot create group: Selected equipment must have the same status");
        return;
      }
      
      const status = uniqueStatuses[0];

      // Additional validation for 'issued' and 'received' statuses - all must be assigned to same person
      if (status === 'issued' || status === 'received') {
        const assignedUsers = selectedEquipmentItems.map(item => item.assigned_to);
        const uniqueUsers = [...new Set(assignedUsers.filter(user => user !== null))];

        if (uniqueUsers.length > 1) {
          toast.error("Cannot create group: Equipment with 'issued' or 'received' status must be assigned to the same person");
          return;
        }
      }

      // Allow group creation for: available, issued, received, service, damaged, out_of_date
      const allowedStatuses = ['available', 'issued', 'received', 'service', 'damaged', 'out_of_date'];
      if (!allowedStatuses.includes(status)) {
        toast.error(`Cannot create group: Equipment with '${status}' status cannot be grouped. Allowed statuses: ${allowedStatuses.join(', ')}`);
        return;
      }
    }
    
    openGroupModal('create', null, selectedEquipmentItems);
  };

  // Handle group inspection - inspect all equipment in the group
  const openGroupInspectionModal = (group) => {
    console.log(`Starting group inspection for group: ${group.name}`);
    console.log('Group items:', group.groupItems);
    
    if (!group.groupItems || group.groupItems.length === 0) {
      toast.warning("No equipment items in this group to inspect");
      return;
    }
    
    // Start multi-equipment inspection mode with all items from the group
    setMultiInspectionMode(true);
    setMultiInspectionQueue([...group.groupItems]); // Create a copy of the array
    setCurrentInspectionIndex(0);
    setSelectedEquipmentForInspection(group.groupItems[0]);
    setShowInspectionModal(true);
    
    console.log(`Started group inspection for ${group.groupItems.length} items`);
  };

  // Handle group assignment - assign all equipment in the group
  const openGroupAssignmentModal = (group) => {
    console.log(`Starting group assignment for group: ${group.name}`);
    console.log('Group items:', group.groupItems);
    
    if (!group.groupItems || group.groupItems.length === 0) {
      toast.warning("No equipment items in this group to assign");
      return;
    }
    
    // Check if all equipment items have 'available' or 'reservation' status
    const unavailableItems = group.groupItems.filter(item =>
      item.safety_equipment_status !== 'available' && item.safety_equipment_status !== 'reservation'
    );
    
    if (unavailableItems.length > 0) {
      // Show modal with unavailable items
      setUnavailableEquipmentList(unavailableItems);
      setShowUnavailableEquipmentModal(true);
      return;
    }
    
    // Store the group information for later use after assignment
    setCurrentAssignmentGroup(group);
    
    // Set selected equipment for assignment to all items in the group
    setSelectedEquipmentForAssignment(group.groupItems);
    setShowAssignmentModal(true);
    
    console.log('Group assignment started with equipment:', group.groupItems);
  };

  // Update group after successful equipment assignment
  const updateGroupAfterAssignment = async (group, userId) => {
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Update group status to 'issued' and assign to user
      await axios.patch(
        `${API_BASE_URL}/safetyequipmentgroup/${group.id}/`,
        { 
          status: 'issued',
          assigned_user: userId
        },
        config
      );

      console.log(`Group ${group.name} successfully assigned to user ${userId} with status 'issued'`);
    } catch (error) {
      console.error('Error updating group after assignment:', error);
      toast.error(`Failed to update group status: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Handle model save
  const handleModelSave = async (modelData) => {
    try {
      // Check if this is just a global copy success notification
      if (modelData && modelData.__globalCopySuccess) {
        // Models were already saved by the copy-global endpoint
        // Just refresh the data and close modal
        await fetchEquipmentData();

        // Force refresh models
        await fetchEquipmentModels(true);

        setShowModelModal(false);
        return;
      }

      const token = localStorage.getItem('access_token');

      // For FormData, don't set Content-Type header - let axios handle it
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        },
      };

      // Validate required data
      if (modelModalMode === 'create' && !selectedTypeForModel) {
        alert('Error: No equipment type selected. Please try again.');
        return;
      }

      // IMPORTANT: selectedTypeForModel contains COMPANY type ID, but models must be linked to GLOBAL types
      // Get the global_type_id from the company type
      let globalTypeId = selectedTypeForModel;
      if (modelModalMode === 'create' && selectedTypeForModel) {
        const companyType = equipmentData.safetyEquipmentTypes?.find(type => type.id === selectedTypeForModel);
        if (companyType && companyType.global_type_id) {
          globalTypeId = companyType.global_type_id;
          console.log('DEBUG: Converting company type ID', selectedTypeForModel, 'to global type ID', globalTypeId);
        }
      }

      let submitData;

      if (modelData instanceof FormData) {
        // Handle FormData submission (with files)
        submitData = modelData;

        if (modelModalMode === 'create') {
          submitData.append('safety_equipment_type', globalTypeId);
        } else {
          // When editing, keep the existing global type (don't change it)
          submitData.append('safety_equipment_type', selectedModel.safety_equipment_type);
        }

        console.log('DEBUG: Submitting FormData with files');
        console.log('DEBUG: selectedTypeForModel (company type):', selectedTypeForModel);
        console.log('DEBUG: globalTypeId (used for model):', globalTypeId);

        // Debug FormData contents being sent to API
        console.log('DEBUG: Final FormData contents being sent:');
        for (let [key, value] of submitData.entries()) {
          console.log(`  ${key}:`, value);
        }
      } else {
        // Handle JSON submission (legacy)
        config.headers['Content-Type'] = 'application/json';

        if (modelModalMode === 'create') {
          submitData = {
            ...modelData,
            safety_equipment_type: globalTypeId,
            company: null  // Will be set automatically by backend
          };
        } else {
          submitData = {
            ...modelData,
            safety_equipment_type: selectedModel.safety_equipment_type,  // Keep existing global type
            company: selectedModel.company || null
          };
        }

        console.log('DEBUG: Submitting JSON data:', submitData);
      }

      if (modelModalMode === 'create') {
        await axios.post(`${API_BASE_URL}/safetyequipmentmodel/`, submitData, config);
      } else {
        await axios.put(`${API_BASE_URL}/safetyequipmentmodel/${selectedModel.id}/`, submitData, config);
      }

      // Refresh data
      await fetchEquipmentData();

      // Force refresh models
      await fetchEquipmentModels(true);

      closeModelModal();
    } catch (error) {
      console.error('Error saving model:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to save model.';
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          // Handle field-specific errors
          const errors = Object.entries(error.response.data).map(([field, messages]) => {
            return `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`;
          }).join('\n');
          errorMessage = `Validation errors:\n${errors}`;
        } else {
          errorMessage = error.response.data.detail || error.response.data;
        }
      }
      
      alert(errorMessage);
    }
  };

  // Handle model delete
  const handleModelDelete = async (model) => {
    setItemToDelete(model);
    setDeleteType('model');

    try {
      // Fetch related items count from backend
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      const response = await axios.get(
        `${API_BASE_URL}/safetyequipmentmodel/${model.id}/delete-check/`,
        config
      );

      if (response.data.equipment_count > 0) {
        setDeleteWarning({
          equipmentCount: response.data.equipment_count
        });
      } else {
        setDeleteWarning(null);
      }
    } catch (error) {
      console.error('Error fetching delete check:', error);
      setDeleteWarning(null);
    }

    setShowDeleteConfirmation(true);
  };

  // Handle delete
  const handleDelete = async (item) => {
    setItemToDelete(item);

    // Check if we're deleting a type (from 'types' tab) or regular equipment
    if (activeTab === 'types') {
      setDeleteType('types');

      try {
        // Fetch related items count from backend
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        };

        const response = await axios.get(
          `${API_BASE_URL}/safetyequipmenttype/${item.id}/delete-check/`,
          config
        );

        if (response.data.models_count > 0 || response.data.equipment_count > 0) {
          setDeleteWarning({
            modelsCount: response.data.models_count,
            equipmentCount: response.data.equipment_count
          });
        } else {
          setDeleteWarning(null);
        }
      } catch (error) {
        console.error('Error fetching delete check:', error);
        setDeleteWarning(null);
      }
    } else {
      setDeleteType('equipment');
      setDeleteWarning(null);
    }

    setShowDeleteConfirmation(true);
  };

  // Handle delete group
  const handleDeleteGroup = (group) => {
    setItemToDelete(group);
    setDeleteType('group');
    setShowDeleteConfirmation(true);
  };

  // Handle restore deleted equipment
  const handleRestore = async (item, confirmed = false) => {
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      const response = await axios.post(
        `${API_BASE_URL}/safetyequipment/${item.id}/restore/`,
        { confirm: confirmed },
        config
      );

      // Check if confirmation is required
      if (response.data.requires_confirmation) {
        // Store item and confirmation data, then show modal
        setItemToRestore(item);
        setRestoreConfirmationData(response.data);
        setShowRestoreConfirmation(true);
        return;
      }

      // Restoration successful
      if (response.data.success) {
        let message = response.data.message;

        // Add info if type/model was also restored
        if (response.data.type_restored || response.data.model_restored) {
          message += '\n\nAlso restored:';
          if (response.data.type_restored) {
            message += '\n• Equipment type';
          }
          if (response.data.model_restored) {
            message += '\n• Equipment model';
          }
        }

        toast.success(message);

        // Refresh data
        await fetchEquipmentData();
        await fetchEquipmentModels(true);
        await fetchFilterOptions();
      } else {
        toast.error(response.data.message || 'Failed to restore equipment');
      }
    } catch (error) {
      console.error('Error restoring equipment:', error);
      const errorMessage = error.response?.data?.error || 'Failed to restore equipment';
      toast.error(errorMessage);
    }
  };

  // Confirm and execute restore with type/model restoration
  const confirmRestore = async () => {
    setShowRestoreConfirmation(false);
    if (itemToRestore) {
      await handleRestore(itemToRestore, true);
      setItemToRestore(null);
      setRestoreConfirmationData(null);
    }
  };

  // Handle delete click - unified function for both equipment and groups
  const handleDeleteClick = (item, type) => {
    if (type === 'group') {
      handleDeleteGroup(item);
    } else {
      handleDelete(item);
    }
  };

  // Handle inspection modal
  const openInspectionModal = (equipment) => {
    // Check if multiple items are selected and this equipment is among them
    const selectedEquipmentItems = equipmentData.safetyEquipment.filter(item => 
      selectedItems.has(item.id) && !item.isGroup
    );
    
    if (selectedEquipmentItems.length > 1 && selectedItems.has(equipment.id)) {
      // Multi-equipment inspection mode
      console.log(`Starting multi-equipment inspection for ${selectedEquipmentItems.length} items`);
      setMultiInspectionMode(true);
      setMultiInspectionQueue(selectedEquipmentItems);
      setCurrentInspectionIndex(0);
      setSelectedEquipmentForInspection(selectedEquipmentItems[0]);
    } else {
      // Single equipment inspection mode
      setMultiInspectionMode(false);
      setMultiInspectionQueue([]);
      setCurrentInspectionIndex(0);
      setSelectedEquipmentForInspection(equipment);
    }
    
    setShowInspectionModal(true);
  };

  const closeInspectionModal = () => {
    setShowInspectionModal(false);
    setSelectedEquipmentForInspection(null);
    setMultiInspectionMode(false);
    setMultiInspectionQueue([]);
    setCurrentInspectionIndex(0);
  };

  const nextInspection = () => {
    const nextIndex = currentInspectionIndex + 1;
    if (nextIndex < multiInspectionQueue.length) {
      setCurrentInspectionIndex(nextIndex);
      setSelectedEquipmentForInspection(multiInspectionQueue[nextIndex]);
    } else {
      // All inspections completed
      closeInspectionModal();
      // Clear selections after completing all inspections
      setSelectedItems(new Set());
    }
  };

  const skipInspection = () => {
    nextInspection();
  };

  // Handle assignment modal
  // Handle history modal
  const openHistoryModal = (equipment) => {
    setSelectedEquipmentForHistory(equipment);
    setShowHistoryModal(true);
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedEquipmentForHistory(null);
  };

  const closeEquipmentCheckModal = () => {
    setShowEquipmentCheckModal(false);
    setSelectedEquipmentForCheck(null);
  };

  // Handle inspection notification modal
  const openInspectionNotificationModal = (equipment) => {
    const currentUserCompanyId = getCurrentUserCompanyId();
    
    // Check if multiple items are selected and this equipment is among them
    let equipmentForNotification = [];
    
    if (selectedItems.size > 1 && selectedItems.has(equipment.id)) {
      // Multi-equipment notification mode - get all selected items
      equipmentForNotification = equipmentData.safetyEquipment.filter(item => 
        selectedItems.has(item.id) && !item.isGroup
      );
    } else {
      // Single equipment notification mode
      equipmentForNotification = [equipment];
    }
    
    // Filter equipment that can receive notifications
    const notifiableEquipment = equipmentForNotification.filter(item => {
      const isDifferentCompany = item.company !== currentUserCompanyId;
      const isSameCompanyAndIssuedOrReceived = item.company === currentUserCompanyId && ['issued', 'received'].includes(item.safety_equipment_status);
      return isDifferentCompany || isSameCompanyAndIssuedOrReceived;
    });
    
    if (notifiableEquipment.length === 0) {
      toast.error("No selected equipment is eligible for inspection notifications");
      return;
    }
    
    // Set only the equipment IDs for notification (modal will fetch full data)
    const equipmentIds = notifiableEquipment.map(item => item.id);
    setSelectedEquipmentForNotification(equipmentIds);

    // Clear previous notification settings (will be handled in InspectionNotification component)
    setNotificationCompanyId(null);
    setAssignedUserForNotification(null);

    setShowInspectionNotificationModal(true);
  };

  const closeInspectionNotificationModal = () => {
    setShowInspectionNotificationModal(false);
    setSelectedEquipmentForNotification(null);
    setNotificationCompanyId(null);
    setAssignedUserForNotification(null);
  };

  // Service modal handlers
  const closeServiceModal = () => {
    setShowServiceModal(false);
    setSelectedEquipmentForService(null);
    setServiceModalMode('send'); // Reset to default mode
  };

  // Project Warning Modal handlers
  const closeProjectWarningModal = () => {
    setShowProjectWarningModal(false);
    setProjectWarningEquipment(null);
  };

  const handleEndProjectAssignment = async () => {
    if (!projectWarningEquipment || projectWarningEquipment.length === 0) return;

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // End project assignments for all equipment
      for (const equipment of projectWarningEquipment) {
        // Get all active PROJECT assignments for this equipment
        const assignmentsUrl = `${API_BASE_URL}/equipmentassignment/?safety_equipment=${equipment.id}&status=assigned&assignment_type=project`;
        const assignmentsResponse = await axios.get(assignmentsUrl, config);
        const projectAssignments = assignmentsResponse.data;

        // End each project assignment
        for (const assignment of projectAssignments) {
          await axios.patch(
            `${API_BASE_URL}/equipmentassignment/${assignment.id}/`,
            {
              status: 'returned',
              date_returned: new Date().toISOString()
            },
            config
          );
        }
      }

      toast.success('Project assignments ended successfully');

      // Refresh data
      await fetchEquipmentData();

      // Close warning modal
      closeProjectWarningModal();

      // Now open service modal with the equipment
      setSelectedEquipmentForService(projectWarningEquipment.length === 1 ? projectWarningEquipment[0] : projectWarningEquipment);
      setServiceModalMode('send');
      setShowServiceModal(true);

    } catch (error) {
      console.error('Error ending project assignments:', error);
      toast.error('Failed to end project assignments');
    }
  };

  // Report generation handlers
  const handleReportClick = () => {
    if (selectedItems.size === 0) {
      toast.warning('Please select at least one equipment item to generate a report');
      return;
    }
    setShowReportModal(true);
  };

  const handleGenerateReport = async (format) => {
    if (selectedItems.size === 0) {
      toast.warning('No items selected');
      return;
    }

    setIsGeneratingReport(true);

    try {
      const token = localStorage.getItem('access_token');
      const selectedIds = Array.from(selectedItems);

      const response = await axios.post(
        `${API_BASE_URL}/safetyequipment/generate-report/`,
        {
          equipment_ids: selectedIds,
          format: format // 'excel' or 'pdf'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob' // Important for file download
        }
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Set filename based on format
      const timestamp = new Date().toISOString().split('T')[0];
      const extension = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `equipment_report_${timestamp}.${extension}`);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Report generated successfully (${selectedIds.length} items)`);
      setShowReportModal(false);

    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleServiceConfirm = async (serviceData) => {
    // Check if this is return mode (status='available') or send mode (status='service')
    const isReturnMode = serviceData.status === 'available';

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Handle multiple equipment items or single item
      const equipmentIds = serviceData.equipment_ids || [serviceData.equipment_id];

      if (isReturnMode) {
        // Return from service: clear service fields and change status to available
        const updatePromises = equipmentIds.map(equipmentId =>
          axios.patch(`${API_BASE_URL}/safetyequipment/${equipmentId}/`, {
            safety_equipment_status: 'available',
            service_company: null,
            service_date: null,
            service_notes: null
          }, config)
        );

        await Promise.all(updatePromises);

        // Update local data for all affected items - clear service fields
        setEquipmentData(prevData => ({
          ...prevData,
          safetyEquipment: prevData.safetyEquipment.map(item =>
            equipmentIds.includes(item.id)
              ? {
                  ...item,
                  safety_equipment_status: 'available',
                  service_company_id: null,
                  service_company_name: null,
                  service_date: null,
                  service_notes: null
                }
              : item
          )
        }));

        const message = equipmentIds.length > 1 ?
          `${equipmentIds.length} equipment items returned from service successfully` :
          'Equipment returned from service successfully';
        toast.success(message);
      } else {
        // Send to service mode
        const updatePromises = equipmentIds.map(equipmentId =>
          axios.patch(`${API_BASE_URL}/safetyequipment/${equipmentId}/`, {
            safety_equipment_status: 'service',
            service_company: serviceData.service_company_id,
            service_date: serviceData.service_date,
            service_notes: serviceData.notes
          }, config)
        );

        await Promise.all(updatePromises);

        // Find service company name
        const serviceCompany = serviceCompanies.find(company =>
          company.id === parseInt(serviceData.service_company_id)
        );

        // Update local data for all affected items
        setEquipmentData(prevData => ({
          ...prevData,
          safetyEquipment: prevData.safetyEquipment.map(item =>
            equipmentIds.includes(item.id)
              ? {
                  ...item,
                  safety_equipment_status: 'service',
                  service_company_id: serviceData.service_company_id,
                  service_company_name: serviceCompany?.name || null,
                  service_date: serviceData.service_date,
                  service_notes: serviceData.notes
                }
              : item
          )
        }));

        const message = equipmentIds.length > 1 ?
          `${equipmentIds.length} equipment items sent to service successfully` :
          'Equipment sent to service successfully';
        toast.success(message);
      }

      // Clear selections after bulk operation
      if (equipmentIds.length > 1) {
        setSelectedItems(new Set());
      }

      // Refresh data to ensure we have the latest information
      // IMPORTANT: await to ensure data is refreshed before modal closes
      await fetchEquipmentData();
    } catch (error) {
      console.error('Error updating service status:', error);
      toast.error(isReturnMode ? 'Failed to return equipment from service' : 'Failed to send equipment to service');
      throw error;
    }
  };

  const openAssignmentModal = (equipment) => {
    // Check if multiple items are selected and this equipment is among them
    let equipmentToAssign = [];

    if (selectedItems.size > 1 && selectedItems.has(equipment.id)) {
      // Multi-equipment assignment mode - get all selected items from the full safety equipment list
      equipmentToAssign = equipmentData.safetyEquipment.filter(item => selectedItems.has(item.id));
    } else {
      // Single equipment assignment mode
      equipmentToAssign = [equipment];
    }

    // Check if any equipment already has BOTH assignments (user AND project)
    // These items cannot be assigned again (icon should already be hidden, but double-check)
    const unavailableItems = equipmentToAssign.filter(item => {
      const hasUserAssignment = item.assignment?.user_name;
      const hasProjectAssignment = item.assignment?.project_name;
      return hasUserAssignment && hasProjectAssignment; // Both assignments already exist
    });

    if (unavailableItems.length > 0) {
      // Show modal with unavailable items
      setUnavailableEquipmentList(unavailableItems);
      setShowUnavailableEquipmentModal(true);
      return;
    }

    // Detect existing assignments for the first equipment (or single equipment)
    // This determines which fields should be blocked in the modal
    const firstEquipment = Array.isArray(equipmentToAssign) ? equipmentToAssign[0] : equipmentToAssign;
    const hasUserAssignment = firstEquipment?.assignment?.user_name;
    const hasProjectAssignment = firstEquipment?.assignment?.project_name;

    setExistingAssignments({
      hasUser: !!hasUserAssignment,
      hasProject: !!hasProjectAssignment
    });

    // All items can be assigned, proceed
    if (equipmentToAssign.length > 1) {
      setSelectedEquipmentForAssignment(equipmentToAssign);
    } else {
      setSelectedEquipmentForAssignment(equipmentToAssign[0]);
    }

    setShowAssignmentModal(true);
  };

  const closeAssignmentModal = () => {
    setShowAssignmentModal(false);
    setSelectedEquipmentForAssignment(null);
    setCurrentAssignmentGroup(null);
  };

  // Handle user filter change with special logic for exclusive options
  const handleUserFilterChange = (newValues) => {
    // "__all_assignments__" is exclusive - can't be combined with specific users
    if (newValues.includes('__all_assignments__')) {
      // Check if user just added the exclusive filter (wasn't in previous selection)
      const wasJustSelected = !selectedUserFilter.includes('__all_assignments__');

      if (wasJustSelected) {
        // User just selected "All Assignments" - set only that one, remove everything else
        setSelectedUserFilter(['__all_assignments__']);
      } else {
        // User is trying to add specific users while "All Assignments" is selected
        // Remove "All Assignments" and keep only the specific users
        const regularUsers = newValues.filter(val => val !== '__all_assignments__');
        setSelectedUserFilter(regularUsers);
      }
    } else {
      // No exclusive filter - normal user IDs, allow multiple selection
      setSelectedUserFilter(newValues);
    }
  };

  // Handle equipment return
  // Handle project filter change with special logic for exclusive options
  const handleProjectFilterChange = (newValues) => {
    // Special filters that should be exclusive (can't be combined with others)
    const exclusiveFilters = ['__all_assignments__', '__no_assignments__'];

    // Check if any exclusive filter is in the new values
    const hasExclusiveFilter = newValues.some(val => exclusiveFilters.includes(val));

    if (hasExclusiveFilter) {
      // Check if user just added an exclusive filter (wasn't in previous selection)
      const newExclusiveFilter = newValues.find(val =>
        exclusiveFilters.includes(val) && !selectedProjectFilter.includes(val)
      );

      if (newExclusiveFilter) {
        // User just selected an exclusive filter - set only that one, remove everything else
        setSelectedProjectFilter([newExclusiveFilter]);
      } else {
        // User is trying to add regular projects while exclusive filter is selected
        // Remove the exclusive filter and keep only the regular projects
        const regularProjects = newValues.filter(val => !exclusiveFilters.includes(val));
        setSelectedProjectFilter(regularProjects);
      }
    } else {
      // No exclusive filters - normal project IDs, allow multiple selection
      setSelectedProjectFilter(newValues);
    }
  };

  const handleReturn = (equipment) => {
    // Check if multiple items are selected and this equipment is among them
    // Filter by items that have any assignment (not just by status)
    const selectedEquipmentWithAssignments = Array.from(selectedItems)
      .map(id => equipmentData.safetyEquipment.find(item => item.id === id))
      .filter(item => {
        if (!item) return false;
        const hasUserAssignment = item.assignment?.user_name;
        const hasProjectAssignment = item.assignment?.project_name;
        return hasUserAssignment || hasProjectAssignment;
      });

    let equipmentList;
    if (selectedEquipmentWithAssignments.length > 1 && selectedItems.has(equipment.id)) {
      // Multi-equipment return mode
      equipmentList = selectedEquipmentWithAssignments;
    } else {
      // Single equipment return mode
      equipmentList = [equipment];
    }

    setEquipmentToReturn(equipmentList);

    // Detect assignment types for the first equipment item (for default returnType)
    // In multi-equipment mode, user will need to select what to return
    if (equipmentList.length > 0) {
      const firstEquipment = equipmentList[0];
      const hasUserAssignment = firstEquipment.assignment?.user_name;
      const hasProjectAssignment = firstEquipment.assignment?.project_name;

      // Set default returnType based on what assignments exist
      if (hasUserAssignment && hasProjectAssignment) {
        setReturnType('both'); // Both assignments - let user choose
      } else if (hasUserAssignment) {
        setReturnType('user'); // Only user assignment
      } else if (hasProjectAssignment) {
        setReturnType('project'); // Only project assignment
      } else {
        setReturnType('both'); // No assignments (shouldn't happen, but default to both)
      }
    }

    setShowReturnConfirmation(true);
  };

  const confirmReturn = async () => {
    if (!equipmentToReturn || equipmentToReturn.length === 0) return;

    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Process each equipment in the array
      const returnPromises = equipmentToReturn.map(async (equipment) => {
        // First, find and update assignments for this equipment based on returnType
        try {
          // Get ALL active assignments for this equipment (both user and project)
          const assignmentsUrl = `${API_BASE_URL}/equipmentassignment/?safety_equipment=${equipment.id}&status=assigned`;
          console.log(`🔍 Fetching assignments for equipment ${equipment.id}:`, assignmentsUrl);

          const assignmentsResponse = await axios.get(assignmentsUrl, config);
          const assignments = assignmentsResponse.data;

          console.log(`📝 Found ${assignments.length} assignments for equipment ${equipment.id}:`, assignments);
          console.log(`🎯 Return type selected: ${returnType}`);

          if (assignments.length > 0) {
            // Filter assignments based on returnType
            const assignmentsToReturn = assignments.filter(assignment => {
              if (returnType === 'both') {
                // Return all assignments
                return true;
              } else if (returnType === 'user') {
                // Return only user assignments (assignment_type='user')
                return assignment.assignment_type === 'user';
              } else if (returnType === 'project') {
                // Return only project assignments (assignment_type='project')
                return assignment.assignment_type === 'project';
              }
              return false;
            });

            console.log(`📦 Assignments to return (${assignmentsToReturn.length}/${assignments.length}):`, assignmentsToReturn);

            // Update selected assignments to returned status
            const assignmentUpdatePromises = assignmentsToReturn.map(assignment => {
              console.log(`🔄 Updating assignment ${assignment.id} (type: ${assignment.assignment_type}) to returned status`);
              return axios.patch(`${API_BASE_URL}/equipmentassignment/${assignment.id}/`,
                {
                  status: 'returned',
                  return_date: new Date().toISOString()
                },
                config
              );
            });

            await Promise.all(assignmentUpdatePromises);
            console.log(`✅ Successfully updated ${assignmentsToReturn.length} assignments for equipment ${equipment.id}`);
          } else {
            console.log(`ℹ️ No active assignments found for equipment ${equipment.id}`);
          }
        } catch (assignmentError) {
          console.error(`❌ Failed to update assignments for equipment ${equipment.id}:`, assignmentError);
          // Continue even if assignment update fails
        }

        // NOTE: Equipment status is automatically updated by backend signals
        // - When user assignment is returned, status changes from 'issued' to 'available'
        // - When project assignment is returned, status remains 'available'
        // So we don't need to manually update equipment status here!
      });

      await Promise.all(returnPromises);

      // Clear selections after successful return
      setSelectedItems(new Set());

      // Refresh data before closing modal to ensure we have latest assignment state
      await fetchEquipmentData();
      setShowReturnConfirmation(false);
      setEquipmentToReturn(null);

      const count = equipmentToReturn.length;
      const returnTypeText = returnType === 'user' ? 'from user' : returnType === 'project' ? 'from project' : '';
      toast.success(`${count} equipment item${count > 1 ? 's' : ''} returned ${returnTypeText} successfully`);
    } catch (error) {
      console.error('Error returning equipment:', error);
      toast.error('Failed to return equipment');
      setShowReturnConfirmation(false);
      setEquipmentToReturn(null);
    }
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      const token = localStorage.getItem('access_token');
      
      if (deleteType === 'model') {
        await axios.delete(`${API_BASE_URL}/safetyequipmentmodel/${itemToDelete.id}/`, {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });
      } else if (deleteType === 'group') {
        await axios.delete(`${API_BASE_URL}/safetyequipmentgroup/${itemToDelete.id}/`, {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });
      } else {
        let endpoint = '';

        switch (activeTab) {
          case 'safety':
            endpoint = 'safetyequipment';
            // For safety equipment, update status to 'deleted' instead of deleting
            await axios.patch(`${API_BASE_URL}/${endpoint}/${itemToDelete.id}/`, {
              safety_equipment_status: 'deleted'
            }, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            break;
          case 'types':
            endpoint = 'safetyequipmenttype';
            // For equipment types, still use DELETE (they don't have status field)
            await axios.delete(`${API_BASE_URL}/${endpoint}/${itemToDelete.id}/`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });
            break;
        }
      }

      await fetchEquipmentData(); // Refresh data after delete

      // If model or type was deleted, force refresh models and filter options
      if (deleteType === 'model' || deleteType === 'types') {
        console.log('🔄 Forcing models refresh after delete, deleteType:', deleteType);
        await fetchEquipmentModels(true); // Force refresh
        console.log('✅ Models refreshed');
        await fetchFilterOptions();
        console.log('✅ Filter options refreshed');
      }

      setShowDeleteConfirmation(false);
      setItemToDelete(null);
      setDeleteType('');
      setDeleteWarning(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item: ' + (error.response?.data?.detail || error.message));
      setShowDeleteConfirmation(false);
      setItemToDelete(null);
      setDeleteType('');
      setDeleteWarning(null);
    }
  };

  // Render table based on active tab
  const renderTable = () => {
    const data = paginatedData;
    
    if (data.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-slate-400 mb-2">
            {getTabIcon(activeTab, 'h-12 w-12 mx-auto')}
          </div>
          <p className="text-slate-500">No {getTabLabel(activeTab).toLowerCase()} found</p>
        </div>
      );
    }

    return (
        <table className="w-full bg-white table-fixed">
          <thead className="bg-steel-100 sticky top-0 z-10 border-b-2 border-brand-500">
            <tr>
              {getTableHeaders().map(header => {
                if (header === 'Select') {
                  return (
                    <th
                      key={header}
                      className="w-6 px-2 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider"
                    >
                      <input
                        type="checkbox"
                        checked={isAllSelected()}
                        ref={(input) => {
                          if (input) input.indeterminate = isSomeSelected();
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-steel-300 rounded"
                      />
                    </th>
                  );
                }
                // Check if this header is a company name (for inspection matrix view)
                const isCompanyName = activeTab === 'inspection' &&
                  (user?.company_can_inspection || false) &&
                  header !== 'Equipment Type' &&
                  cooperatingCompanies.some(company => company.name === header);

                // Get the company object for this header if it's a company name
                const companyForHeader = isCompanyName
                  ? cooperatingCompanies.find(c => c.name === header)
                  : null;

                return (
                  <th
                    key={header}
                    className={`px-6 py-3 text-xs font-medium text-slate-700 uppercase tracking-wider ${
                      isCompanyName ? 'text-center' : 'cursor-pointer hover:bg-steel-200 select-none'
                    } ${
                      header === 'Status' ? 'w-32 text-center' :
                      header === 'Actions' && activeTab === 'groups' ? 'w-24 text-center' :
                      header === 'Actions' ? 'w-64 text-center' :
                      header === 'Equipment Count' ? 'w-48 text-center' :
                      header === 'Validity' ? 'w-40 text-center' :
                      header === 'Inspection' ? 'w-40 text-center' :
                      header === 'Type / Brand' && activeTab === 'inspection' ? 'w-96' :
                      header === 'Company' && activeTab === 'inspection' ? 'w-48 text-center' :
                      header === 'Inspection Company' ? 'w-48 text-center' :
                      isCompanyName ? 'w-32' :
                      'text-left'
                    }`}
                    onClick={!isCompanyName ? () => handleSort(header) : undefined}
                  >
                    <div className={`flex items-center space-x-1 ${
                      header === 'Status' || header === 'Actions' || header === 'Validity' || header === 'Inspection' || header === 'Equipment Count' || isCompanyName || (header === 'Company' && activeTab === 'inspection') || header === 'Inspection Company' ? 'justify-center' : ''
                    }`}>
                      <span>{header}</span>
                      {isCompanyName && companyForHeader && (
                        <div className="relative ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCopyDropdown(showCopyDropdown === companyForHeader.id ? null : companyForHeader.id);
                            }}
                            className="text-brand-600 hover:text-brand-800 p-1 rounded hover:bg-brand-50"
                            title="Copy prices from another company"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          {showCopyDropdown === companyForHeader.id && (
                            <div
                              className="absolute top-full right-0 mt-1 w-48 bg-white border border-steel-200 rounded-md shadow-lg z-20"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="py-1">
                                <div className="px-3 py-2 text-xs font-semibold text-slate-700 border-b">
                                  Copy prices from:
                                </div>
                                {cooperatingCompanies
                                  .filter(c => c.id !== companyForHeader.id)
                                  .map(sourceCompany => (
                                    <button
                                      key={sourceCompany.id}
                                      onClick={() => copyPricesFromCompany(sourceCompany.id, companyForHeader.id)}
                                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-steel-200 flex items-center space-x-2"
                                    >
                                      <span>{sourceCompany.name}</span>
                                    </button>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {!isCompanyName && sortField === header && (
                        sortDirection === 'asc' ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-steel-200">
            {data.map((item, index) => (
              <React.Fragment key={item.id || index}>
                {renderTableRow(item)}
                
                {/* Expanded equipment section for groups */}
                {activeTab === 'groups' && expandedGroups.has(item.id) && (
                  <tr>
                    <td colSpan={getTableHeaders().length} className="bg-steel-50 px-6 py-3">
                      <div className="ml-12 relative">
                        {/* Vertical line connecting items */}
                        <div className="absolute left-0 top-0 bottom-0 w-px bg-steel-300"></div>

                        <div className="pl-6">
                          <h4 className="text-sm font-medium text-slate-800 mb-3">Equipment in {item.name}</h4>

                          {(() => {
                            // Use equipment_items from API (not local filtering which only sees current page due to pagination)
                            const groupEquipment = item.equipment_items || [];

                            if (groupEquipment.length === 0) {
                              return <p className="text-sm text-slate-500 italic">No equipment in this group</p>;
                            }

                            return (
                              <div className="space-y-2">
                                {groupEquipment.map((eq, idx) => (
                                  <div key={eq.id} className="relative flex items-center">
                                    {/* Horizontal branch line */}
                                    <div className="absolute -left-6 top-1/2 w-5 h-px bg-steel-300"></div>
                                    {/* Node dot */}
                                    <div className="absolute -left-[26px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-blue-400 border-2 border-white shadow-sm"></div>

                                    <div className="flex-1 flex items-center justify-between bg-white p-3 rounded border border-steel-200 shadow-sm ml-1">
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-4">
                                          <span className="font-medium text-sm">
                                            {eq.type_name || eq.safety_equipment_type_name || '-'}
                                          </span>
                                          <span className="text-sm text-slate-600">
                                            {eq.brand || eq.safety_equipment_model_brand} {eq.model_name || eq.safety_equipment_model_name}
                                          </span>
                                          <span className="text-xs text-slate-500">
                                            #{eq.serial_number || eq.safety_equipment_serial_number || '-'}
                                          </span>
                                          <span className={`text-xs px-2 py-1 rounded ${
                                            (eq.status || eq.safety_equipment_status) === 'available' ? 'bg-green-100 text-green-800' :
                                            (eq.status || eq.safety_equipment_status) === 'issued' ? 'bg-blue-100 text-blue-800' :
                                            (eq.status || eq.safety_equipment_status) === 'service' ? 'bg-orange-100 text-orange-800' :
                                            (eq.status || eq.safety_equipment_status) === 'damaged' ? 'bg-red-100 text-red-800' :
                                            'bg-steel-100 text-slate-800'
                                          }`}>
                                            {eq.status || eq.safety_equipment_status || '-'}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="flex space-x-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openModal('edit', eq);
                                          }}
                                          className="text-amber-600 hover:text-amber-900"
                                          title="Edit Equipment"
                                        >
                                          <Edit className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openHistoryModal(eq);
                                          }}
                                          className="text-slate-600 hover:text-slate-800"
                                          title="View History"
                                        >
                                          <History className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Expanded models section for equipment types */}
                {activeTab === 'types' && expandedTypes.has(item.id) && (
                  <tr>
                    <td colSpan={getTableHeaders().length} className="bg-steel-50 px-6 py-1">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-slate-800">Models for {item.name}</h4>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openModelModal('create', item.id);
                            }}
                            className="text-sm bg-slate-700 hover:bg-slate-800 text-white px-3 py-1 rounded-md flex items-center space-x-1"
                          >
                            <Plus className="h-3 w-3" />
                            <span>Add Model</span>
                          </button>
                        </div>
                        
                        {getModelsForType(item.id).length === 0 ? (
                          <p className="text-sm text-slate-500 italic">No models defined for this equipment type</p>
                        ) : (
                          <div className="space-y-2">
                            {getModelsForType(item.id).map((model) => (
                              <div key={model.id} className="flex items-center justify-between bg-white p-3 rounded border">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-4">
                                    <span className="font-medium text-sm">{model.brand} {model.name}</span>
                                    {model.manufacturer && (
                                      <span className="text-xs text-slate-500">by {model.manufacturer}</span>
                                    )}
                                    {model.price && (
                                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                        ${model.price}
                                      </span>
                                    )}
                                    {/* Validity badge - red if not set, blue if set */}
                                    {model.time_to_manufacturer_inspection ? (
                                      <span className="text-xs bg-brand-100 text-brand-700 px-2 py-1 rounded" title="Service life">
                                        Service life: {model.time_to_manufacturer_inspection} {model.time_to_manufacturer_inspection === 1 ? 'year' : 'years'}
                                      </span>
                                    ) : (
                                      <span className="text-xs bg-red-600 text-white px-2 py-1 rounded font-medium" title="No validity period set for this model">
                                        No validity set
                                      </span>
                                    )}
                                  </div>
                                  {model.description && (
                                    <p className="text-xs text-slate-600 mt-1">{model.description}</p>
                                  )}
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModelModal('view', item.id, model);
                                    }}
                                    className="text-brand-600 hover:text-brand-800"
                                    title="View Model"
                                  >
                                    <Eye className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openModelModal('edit', item.id, model);
                                    }}
                                    className="text-yellow-600 hover:text-yellow-900"
                                    title="Edit Model"
                                  >
                                    <Edit className="h-3 w-3" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleModelDelete(model);
                                    }}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete Model"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
    );
  };


  // Get table headers based on active tab
  const getTableHeaders = () => {
    const canInspection = user?.company_can_inspection || false;
    
    switch (activeTab) {
      case 'safety':
        const safetyHeaders = ['Select', 'Type / Brand', 'Model / Serial Number', 'Validity', 'Inspection', 'Company', 'Assignment', 'Group', 'Status', 'Actions'];
        return safetyHeaders;
      case 'groups':
        return ['Name', 'Description', 'Equipment Count', 'Status', 'Actions'];
      case 'inspection':
        if (canInspection) {
          // For companies with can_inspection = true: Equipment Types + Cooperating Companies
          const cooperatingCompanyNames = cooperatingCompanies.map(company => company.name);
          return ['Equipment Type', ...cooperatingCompanyNames];
        } else {
          // For companies with can_inspection = false: Standard inspection view
          const inspectionHeaders = ['Select', 'Type / Brand', 'Model / Serial Number', 'Inspection', 'Company', 'Inspection Company'];
          return inspectionHeaders;
        }
      case 'types':
        return ['Name', 'Description', 'Actions'];
      default:
        return [];
    }
  };

  // Render table row based on active tab
  const renderTableRow = (item) => {
    // Check if this is a critical item: no inspection + status issued/received/private
    // (declared here to be accessible in all case statements)
    const isNoInspectionCritical = !item.latest_inspection?.validity_date &&
                                    (item.safety_equipment_status === 'issued' ||
                                     item.safety_equipment_status === 'received' ||
                                     item.safety_equipment_status === 'private');

    switch (activeTab) {
      case 'safety':
        // Handle individual equipment rows only (no groups)
        const rowClassName = isNoInspectionCritical
          ? "bg-red-50 hover:bg-red-100 cursor-pointer"
          : "bg-white hover:bg-brand-50 cursor-pointer";

        return (
          <tr key={`equipment-${item.id}`} className={rowClassName} onDoubleClick={() => handleEquipmentDoubleClick(item)}>
            <td className="w-6 px-2 py-1 whitespace-nowrap">
              <input
                type="checkbox"
                checked={selectedItems.has(item.id)}
                onChange={() => handleItemSelect(item.id)}
                className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-steel-300 rounded"
              />
            </td>
            <td className="pl-2 pr-6 py-1 min-w-0">
              <div className="flex items-center space-x-3 overflow-hidden">
                {item.safety_equipment_model_image ? (
                  <img
                    src={item.safety_equipment_model_image}
                    alt={item.safety_equipment_type_name || 'Equipment'}
                    className="w-8 h-8 object-cover rounded flex-shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-8 h-8 bg-steel-200 rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-400 text-sm">📦</span>
                  </div>
                )}
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="font-medium truncate" title={item.safety_equipment_type_name || item.safety_equipment_type?.name || '-'}>
                    {item.safety_equipment_type_name || item.safety_equipment_type?.name || '-'}
                  </span>
                  {item.safety_equipment_model_brand && (
                    <span className="text-xs text-slate-500 truncate" title={`${item.safety_equipment_model_brand}${item.global_type_id ? ` (ID: ${item.global_type_id})` : ''}`}>
                      {item.safety_equipment_model_brand}{item.global_type_id && ` (${item.global_type_id})`}
                    </span>
                  )}
                </div>
              </div>
            </td>
            <td className="px-6 py-1 min-w-0">
              <div className="flex flex-col overflow-hidden">
                <span className="font-medium truncate" title={getModelNameFromModel(item)}>
                  {getModelNameFromModel(item)}
                </span>
                {item.safety_equipment_serial_number && (
                  <span className="text-xs text-slate-500 truncate" title={item.safety_equipment_serial_number}>
                    {item.safety_equipment_serial_number}
                  </span>
                )}
              </div>
            </td>
            
            {/* Validity Column */}
            <td className="w-40 px-6 py-1 whitespace-nowrap text-center">
              {(() => {
                const manufacturerStatus = getManufacturerValidityStatus(item);

                // Case 1: No data (model has no time_to_manufacturer_inspection set)
                if (!manufacturerStatus.hasData) {
                  return (
                    <div className="inline-flex px-2 py-1 text-xs rounded-full text-white bg-red-600">
                      No data for this model
                    </div>
                  );
                }

                // Case 2: Lifetime validity (>50 years in the future)
                if (manufacturerStatus.status === 'lifetime') {
                  return (
                    <div className="inline-flex px-2 py-1 text-xs rounded-full text-black bg-green-200">
                      Lifetime
                    </div>
                  );
                }

                // Case 3: Normal validity date
                const mfgStyle = (manufacturerStatus.status === 'expired' ||
                                  manufacturerStatus.status === 'expires_soon' ||
                                  manufacturerStatus.status === 'expires_medium')
                  ? getManufacturerValidityStyle(manufacturerStatus.daysUntilExpiry)
                  : {};
                return (
                  <div
                    className="inline-flex px-2 py-1 text-xs rounded-full flex-col"
                    style={mfgStyle}
                  >
                    <div className="leading-tight">
                      {manufacturerStatus.validityDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="leading-tight">
                      {formatManufacturerDaysStatus(manufacturerStatus.validityDate)}
                    </div>
                  </div>
                );
              })()}
            </td>

            {/* Inspection Column */}
            <td className="w-40 px-6 py-1 whitespace-nowrap text-center">
              {item.latest_inspection?.validity_date ? (
                (() => {
                  const validityDate = new Date(item.latest_inspection.validity_date);
                  const today = new Date();
                  const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));
                  const inspStyle = getInspectionValidityStyle(daysUntilExpiry, item.latest_inspection.status);
                  return (
                    <div
                      className="inline-flex px-2 py-1 text-xs rounded-full flex-col"
                      style={inspStyle}
                    >
                      <div className="leading-tight">
                        {validityDate.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="leading-tight">
                        {formatDaysStatus(item.latest_inspection.validity_date)}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <span className={`text-xs font-semibold ${isNoInspectionCritical ? 'text-red-700' : 'text-slate-400'}`}>
                  No inspection
                </span>
              )}
            </td>
            <td className="px-6 py-1 min-w-0">
              {(() => {
                // Determine access type and display accordingly
                if (isOwner(item)) {
                  // Our equipment - show company name
                  // If we outsource inspections to another company, show that info too
                  if (item.inspection_company_name && item.inspection_company_name !== item.company_name) {
                    return (
                      <div className="flex flex-col overflow-hidden">
                        <span className="font-medium truncate" title={item.company_name || '-'}>
                          {item.company_name || '-'}
                        </span>
                        <span className="text-xs text-slate-500 truncate" title={`Inspection - ${item.inspection_company_name}`}>
                          Inspection - {item.inspection_company_name}
                        </span>
                      </div>
                    );
                  } else {
                    return (
                      <span className="truncate block" title={item.company_name || '-'}>
                        {item.company_name || '-'}
                      </span>
                    );
                  }
                } else if (isInspectionOnly(item)) {
                  // Equipment we have inspection rights for
                  return (
                    <div className="flex flex-col overflow-hidden">
                      <span className="font-medium truncate" title={item.company_name || '-'}>
                        {item.company_name || '-'}
                      </span>
                      <span className="text-xs text-slate-500 italic">
                        Inspection only
                      </span>
                    </div>
                  );
                } else if (item.safety_equipment_status === 'private') {
                  // Private equipment - no company, just show dash
                  return '-';
                } else if (isSharedWithUs(item)) {
                  // Shared equipment
                  return (
                    <div className="flex flex-col overflow-hidden">
                      <span className="text-xs text-slate-500">Shared by</span>
                      <span className="font-medium truncate" title={item.company_name || '-'}>
                        {item.company_name || '-'}
                      </span>
                    </div>
                  );
                } else {
                  // Fallback - shouldn't happen
                  return (
                    <span className="truncate block" title={item.company_name || '-'}>
                      {item.company_name || '-'}
                    </span>
                  );
                }
              })()}
            </td>
            <td className="px-6 py-1 min-w-0">
              {(() => {
                // If equipment is private (shared with company), show personal owner
                if (item.safety_equipment_status === 'private' && item.personal_owner_name) {
                  return (
                    <div className="flex flex-col overflow-hidden">
                      <span className="font-medium truncate" title={item.personal_owner_name}>
                        {item.personal_owner_name}
                      </span>
                      <span className="text-xs text-purple-600 italic">
                        Private item
                      </span>
                    </div>
                  );
                }

                // If equipment is in service, show service company name
                if (item.safety_equipment_status === 'service' && item.service_company_name) {
                  return (
                    <div className="flex flex-col overflow-hidden">
                      <span className="font-medium text-orange-600">Service</span>
                      <span className="text-xs text-slate-500 truncate" title={item.service_company_name}>
                        {item.service_company_name}
                      </span>
                    </div>
                  );
                }

                // Check for assignment data from optimized endpoint
                if (item.assignment || item._assignment_user_name) {
                  const userName = item.assignment?.user_name || item._assignment_user_name || '-';
                  const projectName = item.assignment?.project_name || item._assignment_project_name;

                  return (
                    <div className="flex flex-col overflow-hidden">
                      <span className="font-medium truncate" title={userName}>{userName}</span>
                      {projectName && (
                        <span className="text-xs text-slate-500 truncate" title={projectName}>
                          {projectName}
                        </span>
                      )}
                    </div>
                  );
                }

                return '-';
              })()}
            </td>
            <td className="px-6 py-1 min-w-0">
              {(() => {
                // Check if equipment has group data from optimized endpoint
                if (item.equipment_group_id && item.equipment_group_name) {
                  // Find full group object for modal
                  const group = equipmentData?.safetyEquipmentGroups?.find(g => g.id === item.equipment_group_id);

                  return (
                    <button
                      onClick={() => group && openGroupModal('view', group)}
                      className="text-brand-600 hover:text-brand-800 hover:underline font-medium truncate w-full text-left"
                      title={`View group: ${item.equipment_group_name}`}
                    >
                      {item.equipment_group_name}
                    </button>
                  );
                }

                return '-';
              })()}
            </td>
            <td className="w-32 px-6 py-1 whitespace-nowrap text-center">
              <select
                value={item.safety_equipment_status || 'Unknown'}
                onChange={(e) => handleStatusChange(item.id, e.target.value)}
                disabled={isInspectionOnly(item)}
                className={`w-full px-2 py-1 pr-2 text-xs font-semibold rounded-md border ${getStatusColor(item.safety_equipment_status)} ${isInspectionOnly(item) ? 'bg-steel-100 text-slate-500 cursor-not-allowed' : ''}`}
                style={{ minWidth: '100px' }}
                onFocus={() => console.log('DEBUG: Item status on focus:', item.id, item.safety_equipment_status)}
                title={isInspectionOnly(item) ? 'Status cannot be changed - equipment is assigned for inspection only' : ''}
              >
                {/* Show current status always */}
                <option value={item.safety_equipment_status} style={{color: 'black'}}>
                  {(() => {
                    switch(item.safety_equipment_status) {
                      case 'available': return 'Available';
                      case 'service': return 'Service';
                      case 'reservation': return 'Reservation';
                      case 'issued': return 'Issued';
                      case 'damaged': return 'Damaged';
                      case 'out_of_date': return 'Out of date';
                      case 'received': return 'Received';
                      case 'returned': return 'Returned';
                      case 'deleted': return 'Deleted';
                      case 'private': return 'Private';
                      default: return item.safety_equipment_status;
                    }
                  })()}
                </option>

                {/* Only show allowed transitions if user can modify status (not inspection-only) */}
                {!isInspectionOnly(item) && (
                  <>
                    {/* Only show allowed transitions for 'available' status */}
                    {item.safety_equipment_status === 'available' && (
                      <>
                        <option value="reservation" style={{color: 'black'}}>Reservation</option>
                        <option value="damaged" style={{color: 'black'}}>Damaged</option>
                        <option value="service" style={{color: 'black'}}>Service</option>
                        <option value="issued" style={{color: 'black'}}>Issued</option>
                        <option value="out_of_date" style={{color: 'black'}}>Out of date</option>
                      </>
                    )}

                    {/* Only show allowed transitions for 'service' status */}
                    {item.safety_equipment_status === 'service' && (
                      <option value="available" style={{color: 'black'}}>Available</option>
                    )}

                    {/* Only show allowed transitions for 'reservation' status */}
                    {item.safety_equipment_status === 'reservation' && (
                      <>
                        <option value="available" style={{color: 'black'}}>Available</option>
                        <option value="damaged" style={{color: 'black'}}>Damaged</option>
                        <option value="service" style={{color: 'black'}}>Service</option>
                        <option value="issued" style={{color: 'black'}}>Issued</option>
                        <option value="out_of_date" style={{color: 'black'}}>Out of date</option>
                      </>
                    )}

                    {/* Only show allowed transitions for 'issued' status */}
                    {item.safety_equipment_status === 'issued' && (
                      <option value="available" style={{color: 'black'}}>Available</option>
                    )}

                    {/* Only show allowed transitions for 'damaged' status */}
                    {item.safety_equipment_status === 'damaged' && (
                      <>
                        <option value="available" style={{color: 'black'}}>Available</option>
                        <option value="service" style={{color: 'black'}}>Service</option>
                      </>
                    )}

                    {/* Only show allowed transitions for 'received' status */}
                    {item.safety_equipment_status === 'received' && (
                      <option value="available" style={{color: 'black'}}>Available</option>
                    )}

                    {/* Only show allowed transitions for 'returned' status */}
                    {item.safety_equipment_status === 'returned' && (
                      <option value="available" style={{color: 'black'}}>Available</option>
                    )}
                  </>
                )}
              </select>
            </td>
            <td className="w-48 px-6 py-1 whitespace-nowrap text-center text-sm font-medium">
              <div className="flex items-center justify-end space-x-2">
                {(() => {
                  // Check if equipment belongs to current user's company or is shared with us
                  const isEquipmentOwner = isOwner(item);
                  const hasAccessToEquipment = canAccessEquipment(item);
                  const isDeleted = item.safety_equipment_status === 'deleted';
                  const isInService = item.safety_equipment_status === 'service';
                  const isPrivate = item.safety_equipment_status === 'private';

                  // Special actions for private equipment - inspection, history, and notification
                  if (isPrivate) {
                    return (
                      <>
                        {/* Inspection button - for inspectors only */}
                        {user?.inspector && (
                          <button
                            onClick={() => openInspectionModal(item)}
                            className="text-brand-600 hover:text-brand-800 relative"
                            title={selectedItems.size > 1 && selectedItems.has(item.id)
                              ? `Inspect ${selectedItems.size} selected items`
                              : "Inspection"}
                          >
                            <Calendar className="h-4 w-4" />
                            {selectedItems.size > 1 && selectedItems.has(item.id) && (
                              <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                                {selectedItems.size}
                              </span>
                            )}
                          </button>
                        )}

                        {/* History button */}
                        <button
                          onClick={() => openHistoryModal(item)}
                          className="text-slate-600 hover:text-slate-800"
                          title="View History"
                        >
                          <History className="h-4 w-4" />
                        </button>

                        {/* Inspection notification button */}
                        <button
                          onClick={() => openInspectionNotificationModal(item)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Send Inspection Notification"
                        >
                          <Bell className="h-4 w-4" />
                        </button>
                      </>
                    );
                  }

                  // Special actions for equipment in service status
                  if (isInService && hasAccessToEquipment) {
                    // Count how many selected items have 'service' status
                    const selectedServiceCount = Array.from(selectedItems).filter(id => {
                      const selectedItem = equipmentData.safetyEquipment.find(eq => eq.id === id);
                      return selectedItem && selectedItem.safety_equipment_status === 'service';
                    }).length;

                    return (
                      <>
                        {/* Return from service button */}
                        <button
                          onClick={() => {
                            // Check if there are multiple selected items and this item is one of them
                            if (selectedItems.size > 0 && selectedItems.has(item.id)) {
                              // Get all selected equipment items that have 'service' status
                              const selectedServiceEquipment = equipmentData.safetyEquipment.filter(eq =>
                                selectedItems.has(eq.id) && eq.safety_equipment_status === 'service'
                              );
                              setSelectedEquipmentForService(selectedServiceEquipment);
                            } else {
                              // Single equipment selection
                              setSelectedEquipmentForService(item);
                            }
                            setServiceModalMode('return');
                            setShowServiceModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 relative"
                          title={selectedItems.size > 1 && selectedItems.has(item.id)
                            ? `Return ${selectedServiceCount} selected items from service`
                            : "Return from Service"}
                        >
                          <PackageCheck className="h-4 w-4" />
                          {selectedItems.size > 1 && selectedItems.has(item.id) && selectedServiceCount > 1 && (
                            <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                              {selectedServiceCount}
                            </span>
                          )}
                        </button>

                        {/* History button */}
                        <button
                          onClick={() => openHistoryModal(item)}
                          className="text-slate-600 hover:text-slate-800"
                          title="View History"
                        >
                          <History className="h-4 w-4" />
                        </button>

                        {/* Delete button - not for shared equipment */}
                        {!isSharedWithUs(item) && (
                          <button
                            onClick={() => handleDeleteClick(item, 'equipment')}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Equipment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </>
                    );
                  }

                  // Normal actions for other statuses
                  return (
                    <>
                      {/* Edit button - for owners only (not shared equipment) and not deleted */}
                      {hasAccessToEquipment && !isDeleted && !isSharedWithUs(item) && (
                        <button
                          onClick={() => openModal('edit', item)}
                          className="text-amber-600 hover:text-amber-900"
                          title="Edit Equipment"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}

                      {/* Duplicate button - only for equipment OWNERS (not shared or inspection-only) and not deleted */}
                      {isEquipmentOwner && !isDeleted && (
                        <button
                          onClick={() => openModal('duplicate', item)}
                          className="text-green-600 hover:text-green-900"
                          title="Duplicate Equipment"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      )}

                      {/* Inspection button - for inspectors with access OR inspection rights, not for out_of_date items, and not deleted */}
                      {user?.inspector && (hasAccessToEquipment || hasInspectionRights(item)) && item.safety_equipment_status !== 'out_of_date' && !isDeleted && (
                        <button
                          onClick={() => openInspectionModal(item)}
                          className="text-brand-600 hover:text-brand-800 relative"
                          title={selectedItems.size > 1 && selectedItems.has(item.id)
                            ? `Inspect ${selectedItems.size} selected items`
                            : "Inspection"}
                        >
                          <Calendar className="h-4 w-4" />
                          {selectedItems.size > 1 && selectedItems.has(item.id) && (
                            <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                              {selectedItems.size}
                            </span>
                          )}
                        </button>
                      )}

                      {/* Assignment button - show when no assignment OR only one assignment (not both) */}
                      {hasAccessToEquipment && !isDeleted && (() => {
                        const hasUserAssignment = item.assignment?.user_name;
                        const hasProjectAssignment = item.assignment?.project_name;
                        const hasBothAssignments = hasUserAssignment && hasProjectAssignment;

                        // Show when not both are assigned (0 or 1 assignment)
                        return !hasBothAssignments;
                      })() && (
                        <button
                          onClick={() => openAssignmentModal(item)}
                          className="text-slate-600 hover:text-slate-800 relative"
                          title={selectedItems.size > 1 && selectedItems.has(item.id)
                            ? `Assign ${selectedItems.size} selected items`
                            : "Assign Equipment"}
                        >
                          <UserPlus className="h-4 w-4" />
                          {selectedItems.size > 1 && selectedItems.has(item.id) && (
                            <span className="absolute -top-2 -right-2 bg-indigo-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                              {selectedItems.size}
                            </span>
                          )}
                        </button>
                      )}

                      {/* Return button - show when at least one assignment exists */}
                      {hasAccessToEquipment && !isDeleted && (() => {
                        const hasUserAssignment = item.assignment?.user_name;
                        const hasProjectAssignment = item.assignment?.project_name;
                        const hasAnyAssignment = hasUserAssignment || hasProjectAssignment;

                        // Show when any assignment exists (user OR project OR both)
                        return hasAnyAssignment;
                      })() && (
                        <button
                          onClick={() => handleReturn(item)}
                          className="text-green-600 hover:text-green-900 relative"
                          title={
                            selectedItems.has(item.id) && selectedItems.size > 1
                              ? `Mark as Returned (${selectedItems.size} selected items)`
                              : "Mark as Returned"
                          }
                        >
                          <ArrowRight className="h-4 w-4" />
                          {selectedItems.has(item.id) && selectedItems.size > 1 && (
                            <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                              {selectedItems.size}
                            </span>
                          )}
                        </button>
                      )}
                      
                      {/* History button - always available */}
                      <button
                        onClick={() => openHistoryModal(item)}
                        className="text-slate-600 hover:text-slate-800"
                        title="View History"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      
                      {/* Inspection notification button - show for different company OR same company with issued/received status, but NOT for shared equipment */}
                      {(() => {
                        const currentUserCompanyId = getCurrentUserCompanyId();
                        const isDifferentCompany = item.company !== currentUserCompanyId;
                        const isSameCompanyAndIssuedOrReceived = item.company === currentUserCompanyId && ['issued', 'received'].includes(item.safety_equipment_status);

                        // Count eligible selected items for notification
                        const selectedNotifiableCount = Array.from(selectedItems).filter(id => {
                          const selectedItem = equipmentData.safetyEquipment.find(eq => eq.id === id);
                          if (!selectedItem) return false;
                          const isSelectedDifferentCompany = selectedItem.company !== currentUserCompanyId;
                          const isSelectedSameCompanyAndIssuedOrReceived = selectedItem.company === currentUserCompanyId && ['issued', 'received'].includes(selectedItem.safety_equipment_status);
                          return isSelectedDifferentCompany || isSelectedSameCompanyAndIssuedOrReceived;
                        }).length;

                        return (isDifferentCompany || isSameCompanyAndIssuedOrReceived) && !isSharedWithUs(item) && (
                          <button
                            onClick={() => openInspectionNotificationModal(item)}
                            className="text-orange-600 hover:text-orange-900 relative"
                            title={selectedItems.size > 1 && selectedItems.has(item.id) 
                              ? `Send notification for ${selectedNotifiableCount} selected items` 
                              : "Send Inspection Notification"}
                          >
                            <Bell className="h-4 w-4" />
                            {selectedItems.size > 1 && selectedItems.has(item.id) && selectedNotifiableCount > 1 && (
                              <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-bold">
                                {selectedNotifiableCount}
                              </span>
                            )}
                          </button>
                        );
                      })()}

                      {/* Delete button - for owners only (not shared equipment) and not already deleted */}
                      {hasAccessToEquipment && !isDeleted && !isSharedWithUs(item) && (
                        <button
                          onClick={() => handleDeleteClick(item, 'equipment')}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Equipment"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}

                      {/* Restore button - for deleted equipment, only for owners */}
                      {hasAccessToEquipment && isDeleted && (
                        <button
                          onClick={() => handleRestore(item)}
                          className="text-brand-600 hover:text-brand-800"
                          title="Restore Equipment"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </td>
          </tr>
        );
      case 'inspection':
        const canInspection = user?.company_can_inspection || false;

        if (canInspection) {
          // For companies with can_inspection = true: render equipment type row with price input columns
          return (
            <tr key={`inspection-type-${item.id}`} className="bg-white hover:bg-brand-50">
              <td className="px-6 py-4 font-medium text-slate-800">
                {item.name || item.safety_equipment_type_name || '-'}
              </td>
              {cooperatingCompanies.map(company => {
                const priceKey = `${item.id}-${company.id}`;
                // Get current price from state (pending changes take priority)
                const currentPrice = pendingPriceChanges.has(priceKey)
                  ? pendingPriceChanges.get(priceKey)
                  : inspectionPrices[item.id]?.[company.id];

                return (
                  <td key={company.id} className="px-6 py-4 text-center">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={currentPrice?.price || ''}
                      onChange={(e) => handlePriceChange(item.id, item.global_type_id, company.id, e.target.value)}
                      className={`w-24 px-2 py-1 text-center border rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-sm ${
                        pendingPriceChanges.has(priceKey) ? 'border-brand-300 bg-brand-50' : 'border-steel-200'
                      }`}
                    />
                    {pendingPriceChanges.has(priceKey) && (
                      <div className="text-xs text-orange-600 mt-1">*</div>
                    )}
                  </td>
                );
              })}
            </tr>
          );
        } else {
          // For companies with can_inspection = false: standard inspection view
          return (
            <tr key={`inspection-${item.id}`} className="bg-white hover:bg-brand-50 cursor-pointer" onDoubleClick={() => handleEquipmentDoubleClick(item)}>
              <td className="w-6 px-2 py-1 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={selectedItems.has(item.id)}
                  onChange={() => handleItemSelect(item.id)}
                  className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-steel-300 rounded"
                />
              </td>
            <td className="w-96 pl-2 pr-6 py-1 min-w-0">
              <div className="flex items-center space-x-3 overflow-hidden">
                {item.safety_equipment_model_image ? (
                  <img
                    src={item.safety_equipment_model_image}
                    alt={item.safety_equipment_type_name || 'Equipment'}
                    className="w-8 h-8 object-cover rounded flex-shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-8 h-8 bg-steel-200 rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-400 text-sm">📦</span>
                  </div>
                )}
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="font-medium truncate" title={item.safety_equipment_type_name || item.safety_equipment_type?.name || '-'}>
                    {item.safety_equipment_type_name || item.safety_equipment_type?.name || '-'}
                  </span>
                  {item.safety_equipment_model_brand && (
                    <span className="text-xs text-slate-500 truncate" title={`${item.safety_equipment_model_brand}${item.global_type_id ? ` (ID: ${item.global_type_id})` : ''}`}>
                      {item.safety_equipment_model_brand}{item.global_type_id && ` (${item.global_type_id})`}
                    </span>
                  )}
                </div>
              </div>
            </td>
            <td className="px-6 py-1 min-w-0">
              <div className="flex flex-col overflow-hidden">
                <span className="font-medium truncate" title={getModelNameFromModel(item)}>
                  {getModelNameFromModel(item)}
                </span>
                {item.safety_equipment_serial_number && (
                  <span className="text-xs text-slate-500 truncate" title={item.safety_equipment_serial_number}>
                    {item.safety_equipment_serial_number}
                  </span>
                )}
              </div>
            </td>

            {/* Inspection Column */}
            <td className="w-40 px-6 py-1 whitespace-nowrap text-center">
              {item.latest_inspection?.validity_date ? (
                (() => {
                  const validityDate = new Date(item.latest_inspection.validity_date);
                  const today = new Date();
                  const daysUntilExpiry = Math.floor((validityDate - today) / (1000 * 60 * 60 * 24));
                  const inspStyle = getInspectionValidityStyle(daysUntilExpiry, item.latest_inspection.status);
                  return (
                    <div
                      className="inline-flex px-2 py-1 text-xs rounded-full flex-col"
                      style={inspStyle}
                    >
                      <div className="leading-tight">
                        {validityDate.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="leading-tight">
                        {formatDaysStatus(item.latest_inspection.validity_date)}
                      </div>
                    </div>
                  );
                })()
              ) : (
                <span className={`text-xs font-semibold ${isNoInspectionCritical ? 'text-red-700' : 'text-slate-400'}`}>
                  No inspection
                </span>
              )}
            </td>
            <td className="w-48 px-6 py-1 min-w-0 text-center">
              {(() => {
                // Determine access type and display accordingly
                if (isOwner(item)) {
                  // Our equipment - show company name
                  // If we outsource inspections to another company, show that info too
                  if (item.inspection_company_name && item.inspection_company_name !== item.company_name) {
                    return (
                      <div className="flex flex-col overflow-hidden items-center">
                        <span className="font-medium truncate" title={item.company_name || '-'}>
                          {item.company_name || '-'}
                        </span>
                        <span className="text-xs text-slate-500 truncate" title={`Inspection - ${item.inspection_company_name}`}>
                          Inspection - {item.inspection_company_name}
                        </span>
                      </div>
                    );
                  } else {
                    return (
                      <span className="truncate block" title={item.company_name || '-'}>
                        {item.company_name || '-'}
                      </span>
                    );
                  }
                } else if (isInspectionOnly(item)) {
                  // Equipment we have inspection rights for
                  return (
                    <div className="flex flex-col overflow-hidden items-center">
                      <span className="font-medium truncate" title={item.company_name || '-'}>
                        {item.company_name || '-'}
                      </span>
                      <span className="text-xs text-slate-500 italic">
                        Inspection only
                      </span>
                    </div>
                  );
                } else if (isSharedWithUs(item)) {
                  // Shared equipment
                  return (
                    <div className="flex flex-col overflow-hidden items-center">
                      <span className="text-xs text-slate-500">Shared by</span>
                      <span className="font-medium truncate" title={item.company_name || '-'}>
                        {item.company_name || '-'}
                      </span>
                    </div>
                  );
                } else {
                  // Fallback - shouldn't happen
                  return (
                    <span className="truncate block" title={item.company_name || '-'}>
                      {item.company_name || '-'}
                    </span>
                  );
                }
              })()}
            </td>
            <td className="w-48 px-6 py-1 whitespace-nowrap text-right">
              <div className="flex items-center justify-end space-x-3">
                <select
                  value={pendingInspectionChanges.get(item.id) !== undefined ? pendingInspectionChanges.get(item.id) || '' : item.inspection_company || ''}
                  onChange={(e) => handleInspectionCompanyChange(item.id, e.target.value)}
                  className={`pl-3 pr-8 py-1 border rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-sm min-w-[140px] ${
                    pendingInspectionChanges.has(item.id) ? 'border-brand-300 bg-brand-50' : 'border-steel-200'
                  }`}
                >
                  <option value="">Select Company</option>
                  {cooperatingCompanies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                              </div>
            </td>
          </tr>
        );
        }
      case 'groups':
        // Use equipment_count from API (not local filtering which only sees current page due to pagination)
        const equipmentCount = item.equipment_count || 0;
        const isExpanded = expandedGroups.has(item.id);
        return (
          <tr
            key={`group-${item.id}`}
            className="bg-white hover:bg-brand-50 cursor-pointer"
            onClick={() => toggleGroupExpansion(item.id)}
            onDoubleClick={() => openGroupModal('edit', item)}
          >
            <td className="font-medium px-6 py-4 min-w-0">
              <div className="flex items-center space-x-2 overflow-hidden">
                <ChevronExpand
                  className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
                <FolderOpen className={`h-5 w-5 flex-shrink-0 ${isExpanded ? 'text-brand-600' : 'text-brand-500'}`} />
                <span className="truncate" title={item.name || '-'}>{item.name || '-'}</span>
              </div>
            </td>
            <td className="px-6 py-4 min-w-0">
              <span className="truncate block text-slate-600" title={item.description || '-'}>
                {item.description || '-'}
              </span>
            </td>
            <td className="w-48 px-2 py-4 text-center">
              <span className="bg-brand-100 text-brand-700 text-sm font-medium px-2 py-1 rounded-full">
                {equipmentCount}
              </span>
            </td>
            <td className="w-32 px-2 py-4 text-center">
              <span className={`text-xs px-2 py-1 rounded font-medium ${
                item.status === 'available' ? 'bg-green-100 text-green-800' :
                item.status === 'issued' ? 'bg-blue-100 text-blue-800' :
                item.status === 'received' ? 'bg-purple-100 text-purple-800' :
                item.status === 'service' ? 'bg-orange-100 text-orange-800' :
                item.status === 'reservation' ? 'bg-yellow-100 text-yellow-800' :
                item.status === 'damaged' ? 'bg-red-100 text-red-800' :
                item.status === 'returned' ? 'bg-steel-100 text-slate-800' :
                item.status === 'out_of_date' ? 'bg-red-100 text-red-800' :
                item.status === 'deleted' ? 'bg-red-200 text-red-900' :
                'bg-steel-100 text-slate-800'
              }`}>
                {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1).replace('_', ' ') : '-'}
              </span>
            </td>
            <td className="w-24 px-2 py-4 whitespace-nowrap text-center text-sm font-medium">
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={(e) => { e.stopPropagation(); openGroupModal('edit', item); }}
                  className="text-amber-600 hover:text-amber-900"
                  title="Edit Group"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteClick(item, 'group'); }}
                  className="text-red-600 hover:text-red-900"
                  title="Delete Group"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </td>
          </tr>
        );
      case 'types':
        return (
          <tr key={`type-${item.id}`} className="bg-white hover:bg-brand-50 cursor-pointer" onClick={() => toggleTypeExpansion(item.id)}>
            <td className="font-medium px-6 py-1 min-w-0">
              <div className="flex items-center space-x-2 overflow-hidden">
                <div className="text-slate-400 flex-shrink-0">
                  <ChevronExpand
                    className={`h-4 w-4 transition-transform ${
                      expandedTypes.has(item.id) ? 'rotate-90' : ''
                    }`}
                  />
                </div>
                <span className="truncate" title={item.name || '-'}>{item.name || '-'}</span>
                <span className="bg-brand-100 text-brand-700 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0">
                  {getModelsForType(item.id).length}
                </span>
              </div>
            </td>
            <td className="px-6 py-1 min-w-0">
              <span className="truncate block" title={item.description || '-'}>
                {item.description || '-'}
              </span>
            </td>
            <td className="w-48 px-6 py-1 whitespace-nowrap text-right text-sm font-medium">
              <div className="flex items-center justify-end space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openModelModal('create', item.id);
                  }}
                  className="text-brand-600 hover:text-brand-800"
                  title="Add Model"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openTypeModal('edit', item);
                  }}
                  className="text-amber-600 hover:text-amber-900"
                  title="Edit Type"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item);
                  }}
                  className="text-red-600 hover:text-red-900"
                  title="Delete Type"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </td>
          </tr>
        );
      default:
        return null;
    }
  };

  // Group expand/collapse functions
  const toggleGroupExpansion = (groupId) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (newExpandedGroups.has(groupId)) {
      newExpandedGroups.delete(groupId);
    } else {
      newExpandedGroups.add(groupId);
    }
    setExpandedGroups(newExpandedGroups);
  };

  // Helper functions
  const getTabIcon = (tab, className = "h-5 w-5") => {
    switch (tab) {
      case 'safety': return <Shield className={className} />;
      case 'groups': return <FolderOpen className={className} />;
      case 'inspection': return <Calendar className={className} />;
      case 'types': return <Wrench className={className} />;
      default: return <Wrench className={className} />;
    }
  };

  const getTabLabel = (tab) => {
    switch (tab) {
      case 'safety': return 'Safety Equipment';
      case 'groups': return 'Groups';
      case 'inspection': return 'Inspection Management';
      case 'types': return 'Equipment Types & Models';
      default: return 'Equipment';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available': return 'bg-white text-green-600 font-bold';
      case 'service': return 'bg-white text-orange-600 font-bold';
      case 'reservation': return 'bg-white text-blue-600 font-bold';
      case 'issued': return 'bg-white text-purple-600 font-bold';
      case 'damaged': return 'bg-white text-red-600 font-bold';
      case 'out_of_date': return 'bg-white text-slate-600 font-bold';
      case 'received': return 'bg-white text-teal-600 font-bold';
      case 'returned': return 'bg-white text-indigo-600 font-bold';
      case 'deleted': return 'bg-red-100 text-red-800 font-bold border-red-300';
      default: return 'bg-white text-slate-600 font-bold';
    }
  };

  const getConditionColor = (condition) => {
    switch (condition?.toLowerCase()) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'fair': return 'bg-yellow-100 text-yellow-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-steel-100 text-slate-800';
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col overflow-hidden bg-steel-50">
        <div className="flex-1 bg-white flex flex-col overflow-hidden relative p-4 m-4 rounded-2xl shadow-sm border border-steel-200">

          {/* Tabs and Search */}
          <div className="flex-shrink-0 mb-4">
            <div className="flex items-center justify-between">
              <nav className="border-b border-steel-200 pb-2 flex space-x-8">
          {[
            { key: 'safety', label: 'Safety Equipment' },
            { key: 'groups', label: 'Groups' },
            { key: 'inspection', label: 'Inspection Management' },
            { key: 'types', label: 'Equipment Types & Models' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setSearchTerm(''); // Clear search when switching tabs
                setSelectedTypeFilter([]); // Clear type filter when switching tabs
                setSelectedModelFilter([]); // Clear model filter when switching tabs
                setSelectedStatusFilter([]); // Clear status filter when switching tabs
                setShowDeletedOnly(false); // Clear show deleted status when switching tabs
                setSelectedCompanyFilter([]); // Clear company filter when switching tabs
                setSelectedManufacturerFilter([]); // Clear manufacturer filter when switching tabs
                setSelectedValidityFilter([]); // Clear validity filter when switching tabs
                setSelectedManufacturerValidityFilter([]); // Clear manufacturer validity filter when switching tabs
                setSelectedGroupFilter([]); // Clear group filter when switching tabs
              }}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
              }`}
            >
              {getTabIcon(tab.key)}
              <span>{tab.label}</span>
            </button>
          ))}
              </nav>
              
              {/* Search Bar and Action Buttons */}
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={`Search ${getTabLabel(activeTab).toLowerCase()}... (press Enter to search)`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    autoFocus
                    className="w-100 pl-10 pr-10 py-2 border border-steel-200 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                  />
                  <Search
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                  />
                  {/* Show loading indicator when search is debouncing */}
                  {searchTerm !== debouncedSearchTerm && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                    </div>
                  )}
                  {/* Show clear button when there's text and not loading */}
                  {searchTerm && searchTerm === debouncedSearchTerm && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                      title="Clear search"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>

                {/* Filter Toggle Button */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-xl border transition-colors ${
                    showFilters
                      ? 'bg-brand-50 border-brand-500 text-brand-600'
                      : 'bg-white border-steel-200 text-slate-500 hover:bg-brand-50 hover:border-brand-300'
                  }`}
                  title={showFilters ? 'Hide filters' : 'Show filters'}
                >
                  <Filter className="h-5 w-5" />
                </button>

                {/* Action Buttons */}
                {activeTab === 'inspection' ? (
                  <button
                    onClick={user?.company_can_inspection ? saveInspectionPrices : handleBulkInspectionCompanyAssignment}
                    disabled={user?.company_can_inspection ? pendingPriceChanges.size === 0 : pendingInspectionChanges.size === 0}
                    className="bg-brand-500 hover:bg-brand-600 disabled:bg-steel-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all space-x-2"
                  >
                    <span>
                      {user?.company_can_inspection
                        ? `Save Prices (${pendingPriceChanges.size} pending changes)`
                        : `Save (${pendingInspectionChanges.size} pending changes)`
                      }
                    </span>
                  </button>
                ) : activeTab === 'safety' ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openModal('create')}
                      className="bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all justify-center w-44"
                    >
                      <span>Add Equipment</span>
                    </button>
                    <button
                      onClick={() => {
                        handleCreateGroupClick();
                      }}
                      className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all justify-center w-44"
                    >
                      <span>Create Group</span>
                      {selectedItems.size > 0 && (
                        <span className="bg-brand-500 text-white text-xs px-2 py-1 rounded-full ml-1">
                          {selectedItems.size}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={handleReportClick}
                      className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all justify-center w-44"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Report</span>
                      {selectedItems.size > 0 && (
                        <span className="bg-brand-500 text-white text-xs px-2 py-1 rounded-full ml-1">
                          {selectedItems.size}
                        </span>
                      )}
                    </button>
                  </div>
                ) : activeTab === 'types' ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openTypeModal('create')}
                      className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all space-x-2"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Equipment Type</span>
                    </button>
                    <button
                      onClick={handleImportEquipment}
                      className="bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all space-x-2"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      <span>Import Excel</span>
                    </button>
                    <button
                      onClick={handleExportEquipment}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all space-x-2"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l3-3m0 0l-3-3m3 3H9" />
                      </svg>
                      <span>Export Excel</span>
                    </button>
                    <a
                      href="/templates/safety_equipment_import_template.csv"
                      download="safety_equipment_template.csv"
                      className="bg-steel-600 hover:bg-steel-700 text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all space-x-2"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Template</span>
                    </a>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      if (activeTab === 'groups') {
                        openGroupModal('create');
                      } else {
                        openModal('create');
                      }
                    }}
                    className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-xl flex items-center shadow-lg transition-all space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>
                      {activeTab === 'groups' ? 'Create Group' : 'Add Safety Equipment'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
          <div className="flex-shrink-0 mb-4 flex flex-wrap gap-2 w-full">
          {/* For inspection tab with can_inspection, only show filters if it's the standard view (not price matrix) */}
          {activeTab === 'safety' && (
            <>
              <MultiSelectDropdown
                options={(filterOptions.types || [])
                  .filter(type => type && type.id && type.name)
                  .map(type => ({
                    value: type.id,
                    label: type.name
                  }))}
                value={selectedTypeFilter}
                onChange={setSelectedTypeFilter}
                placeholder="Types"
                className="flex-1 min-w-[150px]"
                searchPlaceholder="Search types..."
              />
              <MultiSelectDropdown
                options={getAvailableManufacturersForFilter()
                  .filter(manufacturer => manufacturer)
                  .map(manufacturer => ({
                    value: manufacturer,
                    label: manufacturer
                  }))}
                value={selectedManufacturerFilter}
                onChange={setSelectedManufacturerFilter}
                placeholder="Manufacturers"
                className="flex-1 min-w-[150px]"
                searchPlaceholder="Search manufacturers..."
              />
              <MultiSelectDropdown
                options={getAvailableModelsForFilter()
                  .filter(model => model && model.id && model.brand && model.name)
                  .map(model => ({
                    value: model.id,
                    label: `${model.brand} ${model.name}`
                  }))}
                value={selectedModelFilter}
                onChange={setSelectedModelFilter}
                placeholder="Models"
                className="flex-1 min-w-[180px]"
                searchPlaceholder="Search models..."
              />
              {(user?.company_can_inspection || false) && (
                <MultiSelectDropdown
                  options={(() => {
                    // Build list: Own company + Cooperating companies
                    const options = [];

                    // Add own company first (if we have the data)
                    if (user?.company && user?.company_name) {
                      const ownCompanyId = typeof user.company === 'number'
                        ? user.company
                        : equipmentData.companies?.find(c => c.name === user.company_name)?.id;

                      if (ownCompanyId) {
                        options.push({
                          value: ownCompanyId,
                          label: `${user.company_name} (My Company)`
                        });
                      }
                    }

                    // Add cooperating companies (avoid duplicates)
                    const ownCompanyId = typeof user?.company === 'number' ? user.company : null;
                    cooperatingCompanies.forEach(company => {
                      if (company && company.id && company.name && company.id !== ownCompanyId) {
                        options.push({
                          value: company.id,
                          label: company.name
                        });
                      }
                    });

                    return options;
                  })()}
                  value={selectedCompanyFilter}
                  onChange={setSelectedCompanyFilter}
                  placeholder="Companies"
                  className="flex-1 min-w-[150px]"
                  searchPlaceholder="Search companies..."
                />
              )}
                <>
                  <MultiSelectDropdown
                    options={[
                      { value: 'available', label: 'Available' },
                      { value: 'service', label: 'Service' },
                      { value: 'reservation', label: 'Reservation' },
                      { value: 'issued', label: 'Issued' },
                      { value: 'damaged', label: 'Damaged' },
                      { value: 'out_of_date', label: 'Out of date' },
                      { value: 'received', label: 'Received' },
                      { value: 'returned', label: 'Returned' },
                      { value: 'private', label: '🔒 Private' },
                      { value: 'show_deleted', label: '🗑️ Show Deleted', className: 'text-red-600 border-t border-steel-200' }
                    ]}
                    value={selectedStatusFilter}
                    onChange={setSelectedStatusFilter}
                    placeholder="Status"
                    className="flex-1 min-w-[120px]"
                    searchPlaceholder="Search status..."
                  />
                  <MultiSelectDropdown
                    options={[
                      { value: 'valid', label: 'Valid (Not Expired)' },
                      { value: 'expired', label: 'Expired' },
                      { value: 'next30', label: 'Expiring in 30 Days' },
                      { value: 'next60', label: 'Expiring in 60 Days' },
                      { value: 'next90', label: 'Expiring in 90 Days' },
                      { value: 'used_without_inspection', label: 'Used Without Inspection' },
                      { value: 'no_inspection', label: 'No Inspection (All)' }
                    ]}
                    value={selectedValidityFilter}
                    onChange={setSelectedValidityFilter}
                    placeholder="Inspection"
                    className="flex-1 min-w-[180px]"
                    searchPlaceholder="Search validity..."
                  />
                  <MultiSelectDropdown
                    options={[
                      { value: 'valid', label: 'Valid (Not Expired)' },
                      { value: 'expired', label: 'Expired' },
                      { value: 'next30', label: 'Expiring in 30 Days' },
                      { value: 'next60', label: 'Expiring in 60 Days' },
                      { value: 'next90', label: 'Expiring in 90 Days' },
                      { value: 'no_data', label: 'No Manufacture Date' }
                    ]}
                    value={selectedManufacturerValidityFilter}
                    onChange={setSelectedManufacturerValidityFilter}
                    placeholder="Validity Date"
                    className="flex-1 min-w-[180px]"
                    searchPlaceholder="Search validity..."
                  />
                </>
              
              
              {/* Group Filter - only for safety tab */}
              {activeTab === 'safety' && (
                <MultiSelectDropdown
                  options={[
                    { value: 'ungrouped', label: 'Ungrouped' },
                    ...(filterOptions.groups || [])
                      .filter(group => group && group.id && group.name)
                      .map(group => ({
                        value: group.id,
                        label: group.name
                      }))
                  ]}
                  value={selectedGroupFilter}
                  onChange={setSelectedGroupFilter}
                  placeholder="Groups"
                  className="flex-1 min-w-[150px]"
                  searchPlaceholder="Search groups..."
                />
              )}

              {/* User Filter - only for safety tab */}
              {activeTab === 'safety' && (
                <MultiSelectDropdown
                  options={[
                    { value: '__all_assignments__', label: '📋 All Assignments (any user)' },
                    ...(filterOptions.users || [])
                      .filter(user => user && user.id && user.name)
                      .map(user => ({
                        value: user.id,
                        label: user.name
                      }))
                  ]}
                  value={selectedUserFilter}
                  onChange={handleUserFilterChange}
                  placeholder="Users"
                  className="flex-1 min-w-[150px]"
                  searchPlaceholder="Search users..."
                />
              )}

              {/* Project Filter - only for safety tab */}
              {activeTab === 'safety' && (
                <MultiSelectDropdown
                  options={[
                    { value: '__all_assignments__', label: '📋 All Assignments (any project)' },
                    { value: '__no_assignments__', label: '🚫 No Project Assignments' },
                    ...(filterOptions.projects || [])
                      .filter(project => project && project.id && project.name)
                      .map(project => ({
                        value: project.id,
                        label: project.name
                      }))
                  ]}
                  value={selectedProjectFilter}
                  onChange={handleProjectFilterChange}
                  placeholder="Projects"
                  className="flex-1 min-w-[150px]"
                  searchPlaceholder="Search projects..."
                />
              )}
            </>
          )}

          {/* Filters for inspection tab - only show if company does NOT have can_inspection */}
          {activeTab === 'inspection' && !(user?.company_can_inspection || false) && (
            <>
              <MultiSelectDropdown
                options={(filterOptions.types || [])
                  .filter(type => type && type.id && type.name)
                  .map(type => ({
                    value: type.id,
                    label: type.name
                  }))}
                value={selectedTypeFilter}
                onChange={setSelectedTypeFilter}
                placeholder="Types"
                className="flex-1 min-w-[150px]"
                searchPlaceholder="Search types..."
              />
              <MultiSelectDropdown
                options={getAvailableManufacturersForFilter()
                  .filter(manufacturer => manufacturer)
                  .map(manufacturer => ({
                    value: manufacturer,
                    label: manufacturer
                  }))}
                value={selectedManufacturerFilter}
                onChange={setSelectedManufacturerFilter}
                placeholder="Manufacturers"
                className="flex-1 min-w-[150px]"
                searchPlaceholder="Search manufacturers..."
              />
              <MultiSelectDropdown
                options={getAvailableModelsForFilter()
                  .filter(model => model && model.id && model.brand && model.name)
                  .map(model => ({
                    value: model.id,
                    label: `${model.brand} ${model.name}`
                  }))}
                value={selectedModelFilter}
                onChange={setSelectedModelFilter}
                placeholder="Models"
                className="flex-1 min-w-[180px]"
                searchPlaceholder="Search models..."
              />
              <MultiSelectDropdown
                options={[
                  { value: 'no_company', label: 'No Inspection Company' },
                  ...cooperatingCompanies.map(company => ({
                    value: company.id.toString(),
                    label: company.name
                  }))
                ]}
                value={selectedCompanyFilter}
                onChange={setSelectedCompanyFilter}
                placeholder="All Companies"
                className="flex-1 min-w-[150px]"
                searchPlaceholder="Search companies..."
              />
            </>
          )}
          </div>
          )}


          {/* Equipment Table */}
          <div className="flex-1 min-h-0 overflow-auto">
            {renderTable()}
          </div>
          
          {/* Pagination - Server Side (for safety and inspection tabs) */}
          {(activeTab === 'safety' || activeTab === 'inspection') && effectiveTotalItems > 0 && (
            <div className="flex-shrink-0 flex items-center justify-between mt-2">
          <div className="flex items-center space-x-4">
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              disabled={loading}
              className={`text-sm pl-4 pr-10 py-1 border border-steel-200 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 max-w-[180px] truncate ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={250}>250 per page</option>
              <option value={500}>500 per page</option>
              <option value={1000}>1000 per page</option>
              <option value={5000}>5000 per page</option>
            </select>
              <div className="text-sm text-[#002D43]">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, effectiveTotalItems)}-
                {Math.min(currentPage * itemsPerPage, effectiveTotalItems)}{' '}
                of <strong>{effectiveTotalItems.toLocaleString()}</strong> {displayingTypes ? 'types' : 'items'}
              </div>
          </div>

          <div className="flex items-center space-x-2">
            {currentPage > 1 && (
              <button
                className={`px-3 py-1 border border-steel-200 text-slate-700 rounded-full hover:bg-brand-50 flex items-center justify-center text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={loading}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>
            )}

            {getVisiblePages().map((page) => (
              <button
                key={page}
                className={`px-3 py-1 border ${
                  page === currentPage
                    ? 'border-steel-200 bg-brand-50 text-brand-600'
                    : 'border-steel-200 text-slate-700'
                } rounded-full hover:bg-brand-50 flex items-center justify-center text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => setCurrentPage(page)}
                disabled={loading}
              >
                {page}
              </button>
            ))}

            {currentPage < totalPages && (
              <button
                className={`px-3 py-1 border border-steel-200 text-slate-700 rounded-full hover:bg-brand-50 flex items-center justify-center text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={loading}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            )}
          </div>
          </div>
          )}
        </div>
      </div>

      {/* Equipment Modal */}
      <EquipmentModal
        isOpen={showModal}
        onClose={closeModal}
        mode={modalMode}
        equipmentType={activeTab === 'groups' ? 'safety' : activeTab}
        selectedItem={selectedItem}
        onSave={handleSave}
        safetyEquipmentTypes={equipmentData.safetyEquipmentTypes}
      />

      {/* Model Modal */}
      <ModelModal
        isOpen={showModelModal}
        onClose={closeModelModal}
        mode={modelModalMode}
        selectedModel={selectedModel}
        equipmentType={equipmentData.safetyEquipmentTypes.find(type => type.id === selectedTypeForModel)}
        onSave={handleModelSave}
      />

      {/* Equipment Type Modal */}
      <EquipmentTypeModal
        isOpen={showTypeModal}
        onClose={closeTypeModal}
        mode={typeModalMode}
        selectedType={selectedType}
        onSave={handleTypeSave}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setItemToDelete(null);
          setDeleteType('');
          setDeleteWarning(null);
        }}
        onConfirm={confirmDelete}
        title={deleteType === 'model' ? "Delete Model" : deleteType === 'group' ? "Delete Group" : deleteType === 'types' ? "Delete Equipment Type" : "Delete Equipment"}
        message={
          <div className="space-y-3">
            <p>Are you sure you want to delete <strong>"{itemToDelete?.name || itemToDelete?.safety_equipment_type_name || 'this item'}"</strong>?</p>

            {deleteWarning && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-yellow-800 mb-1">
                      Warning: Cascade Delete
                    </p>
                    <div className="text-sm text-yellow-700 space-y-1">
                      {deleteType === 'types' && (
                        <>
                          {deleteWarning.modelsCount > 0 && (
                            <p>• <strong>{deleteWarning.modelsCount}</strong> model{deleteWarning.modelsCount !== 1 ? 's' : ''} will be deleted</p>
                          )}
                          {deleteWarning.equipmentCount > 0 && (
                            <p>• <strong>{deleteWarning.equipmentCount}</strong> equipment item{deleteWarning.equipmentCount !== 1 ? 's' : ''} will be deleted</p>
                          )}
                        </>
                      )}
                      {deleteType === 'model' && deleteWarning.equipmentCount > 0 && (
                        <p>• <strong>{deleteWarning.equipmentCount}</strong> equipment item{deleteWarning.equipmentCount !== 1 ? 's' : ''} using this model will be deleted</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <p className="text-slate-600 text-sm">This action cannot be undone.</p>
          </div>
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Inspection Modal */}
      {selectedEquipmentForInspection && (
        <InspectionModal
          isOpen={showInspectionModal}
          onClose={closeInspectionModal}
          equipmentId={selectedEquipmentForInspection.id}
          equipmentName={selectedEquipmentForInspection.safety_equipment_type_name || selectedEquipmentForInspection.name}
          equipmentSerialNumber={selectedEquipmentForInspection.safety_equipment_serial_number}
          equipmentData={selectedEquipmentForInspection}
          onSave={async () => {
            // Refresh data before closing/moving to next to ensure we have latest inspection data
            await fetchEquipmentData();
            if (multiInspectionMode) {
              nextInspection(); // Move to next equipment in queue
            } else {
              closeInspectionModal(); // Close modal for single inspection
            }
          }}
          multiInspectionMode={multiInspectionMode}
          currentInspectionIndex={currentInspectionIndex}
          totalInspections={multiInspectionQueue.length}
          onSkip={skipInspection}
          inspectionTableData={inspectionTableData}
        />
      )}

      {/* Assignment Modal */}
      {selectedEquipmentForAssignment && (
        <AssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => {
            closeAssignmentModal();
            // Clear selections after assignment
            if (Array.isArray(selectedEquipmentForAssignment)) {
              setSelectedItems(new Set());
            }
          }}
          mode="create"
          selectedAssignment={null}
          users={equipmentData.users}
          safetyEquipment={equipmentData.safetyEquipment}
          preSelectedEquipment={selectedEquipmentForAssignment}
          existingAssignments={existingAssignments}
          onSave={async (assignmentData) => {
            // If this was a group assignment, update the group status and assignment
            if (currentAssignmentGroup && assignmentData?.user) {
              await updateGroupAfterAssignment(currentAssignmentGroup, assignmentData.user);
            }

            // Wait for data refresh to complete before closing modal
            await fetchEquipmentData(); // Refresh data after assignment
            closeAssignmentModal();
            // Clear selections after assignment
            if (Array.isArray(selectedEquipmentForAssignment)) {
              setSelectedItems(new Set());
            }
          }}
        />
      )}

      {/* Unavailable Equipment Modal */}
      <ConfirmationModal
        isOpen={showUnavailableEquipmentModal}
        onClose={() => {
          setShowUnavailableEquipmentModal(false);
          setUnavailableEquipmentList([]);
        }}
        onConfirm={() => {
          setShowUnavailableEquipmentModal(false);
          setUnavailableEquipmentList([]);
        }}
        title="Cannot Assign Equipment"
        message={
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              The following items cannot be assigned because they already have both user and project assignments:
            </p>
            <div className="bg-steel-50 p-3 rounded-md max-h-48 overflow-y-auto">
              <ul className="space-y-2">
                {unavailableEquipmentList.map((item, index) => {
                  const brand = getBrandFromModel(item);
                  const model = getModelNameFromModel(item);
                  const displayName = `${item.safety_equipment_type_name || ''} - ${brand} ${model}`.trim();
                  const serialNumber = item.safety_equipment_serial_number ? ` (S/N: ${item.safety_equipment_serial_number})` : '';
                  const userName = item.assignment?.user_name || 'Unknown User';
                  const projectName = item.assignment?.project_name || 'Unknown Project';
                  return (
                    <li key={index} className="flex items-start space-x-2 text-sm">
                      <span className="text-red-500 mt-1">•</span>
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">{displayName}{serialNumber}</div>
                        <div className="text-slate-600 text-xs mt-1">
                          <div>User: <span className="font-medium">{userName}</span></div>
                          <div>Project: <span className="font-medium">{projectName}</span></div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        }
        confirmText="OK"
        cancelText={null}
        variant="warning"
      />

      {/* Return Confirmation Modal */}
      {showReturnConfirmation && equipmentToReturn && (
        <div className="fixed inset-0 bg-slate-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-slate-800">
                {equipmentToReturn.length > 1 ? "Return Multiple Equipment" : "Return Equipment"}
              </h3>
              <button
                onClick={() => {
                  setShowReturnConfirmation(false);
                  setEquipmentToReturn(null);
                }}
                className="text-slate-400 hover:text-slate-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              {(() => {
                // Check if first equipment has both assignments
                const firstEquipment = equipmentToReturn[0];
                const hasUserAssignment = firstEquipment?.assignment?.user_name;
                const hasProjectAssignment = firstEquipment?.assignment?.project_name;
                const hasBothAssignments = hasUserAssignment && hasProjectAssignment;

                if (hasBothAssignments) {
                  // Show options when both assignments exist
                  return (
                    <div>
                      <p className="text-sm text-slate-700 mb-4">
                        This equipment is assigned to both a user and a project. What would you like to return?
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-3 p-3 border rounded-md hover:bg-brand-50 cursor-pointer">
                          <input
                            type="radio"
                            name="returnType"
                            value="user"
                            checked={returnType === 'user'}
                            onChange={(e) => setReturnType(e.target.value)}
                            className="h-4 w-4 text-brand-500 focus:ring-brand-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">Return from User</div>
                            <div className="text-xs text-slate-500">
                              User: {hasUserAssignment}
                            </div>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 p-3 border rounded-md hover:bg-brand-50 cursor-pointer">
                          <input
                            type="radio"
                            name="returnType"
                            value="project"
                            checked={returnType === 'project'}
                            onChange={(e) => setReturnType(e.target.value)}
                            className="h-4 w-4 text-brand-500 focus:ring-brand-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">Return from Project</div>
                            <div className="text-xs text-slate-500">
                              Project: {hasProjectAssignment}
                            </div>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 p-3 border rounded-md hover:bg-brand-50 cursor-pointer">
                          <input
                            type="radio"
                            name="returnType"
                            value="both"
                            checked={returnType === 'both'}
                            onChange={(e) => setReturnType(e.target.value)}
                            className="h-4 w-4 text-brand-500 focus:ring-brand-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-slate-800">Return from Both</div>
                            <div className="text-xs text-slate-500">
                              End both user and project assignments
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  );
                } else {
                  // Simple message when only one assignment
                  return (
                    <p className="text-sm text-slate-700">
                      {equipmentToReturn.length > 1
                        ? `Mark ${equipmentToReturn.length} selected equipment items as returned?`
                        : hasUserAssignment
                          ? `Return this equipment from user: ${hasUserAssignment}?`
                          : hasProjectAssignment
                            ? `Return this equipment from project: ${hasProjectAssignment}?`
                            : "Mark this equipment as returned?"}
                    </p>
                  );
                }
              })()}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReturnConfirmation(false);
                  setEquipmentToReturn(null);
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-steel-200 rounded-md hover:bg-brand-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReturn}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 border border-transparent rounded-xl hover:bg-brand-600 shadow-lg shadow-brand-500/20"
              >
                Return
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRestoreConfirmation}
        onClose={() => {
          setShowRestoreConfirmation(false);
          setItemToRestore(null);
          setRestoreConfirmationData(null);
        }}
        onConfirm={confirmRestore}
        title="Restore Equipment - Confirmation Required"
        message={
          restoreConfirmationData ? (
            <div>
              <p className="mb-3">{restoreConfirmationData.message}</p>
              {restoreConfirmationData.type_is_deleted && (
                <div className="mb-2 text-sm">
                  <span className="font-semibold">Type:</span> {restoreConfirmationData.type_name}
                </div>
              )}
              {restoreConfirmationData.model_is_deleted && (
                <div className="mb-2 text-sm">
                  <span className="font-semibold">Model:</span> {restoreConfirmationData.model_name}
                </div>
              )}
              <p className="mt-3 text-sm text-slate-600">
                These will be restored automatically before restoring the equipment.
              </p>
            </div>
          ) : (
            "Do you want to proceed with restoration?"
          )
        }
        confirmText="Yes, Restore All"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Group Modal */}
      <GroupModal
        isOpen={showGroupModal}
        onClose={closeGroupModal}
        mode={groupModalMode}
        groupData={selectedGroup}
        equipmentData={equipmentData}
        preSelectedEquipment={preSelectedEquipment}
        onSave={handleGroupSave}
        onInspectGroup={openGroupInspectionModal}
        onAssignGroup={openGroupAssignmentModal}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={closeHistoryModal}
        equipment={selectedEquipmentForHistory}
      />

      {/* Equipment Check Modal */}
      <EquipmentCheckModal
        equipmentId={selectedEquipmentForCheck}
        isOpen={showEquipmentCheckModal}
        onClose={closeEquipmentCheckModal}
      />

      {/* Inspection Notification Modal */}
      <InspectionNotification
        isOpen={showInspectionNotificationModal}
        onClose={closeInspectionNotificationModal}
        equipmentIds={selectedEquipmentForNotification}
        companies={equipmentData.companies}
      />

      {/* Project Assignment Warning Modal */}
      {showProjectWarningModal && projectWarningEquipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-slate-800">
                  Equipment Assigned to Project
                </h3>
                <div className="mt-2 text-sm text-slate-600">
                  {projectWarningEquipment.length === 1 ? (
                    <p>
                      This equipment is currently assigned to project: <strong>{projectWarningEquipment[0].assignment?.project_name}</strong>
                    </p>
                  ) : (
                    <p>
                      {projectWarningEquipment.filter(eq => eq.assignment?.project_name).length} item(s) are currently assigned to projects.
                    </p>
                  )}
                  <p className="mt-2">
                    Before sending to service, you must end the project assignment.
                  </p>
                  <p className="mt-2 font-semibold">
                    Do you want to end the project assignment now?
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeProjectWarningModal}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-steel-200 rounded-md hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
              >
                No, Cancel
              </button>
              <button
                onClick={handleEndProjectAssignment}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 border border-transparent rounded-xl hover:bg-brand-600 shadow-lg shadow-brand-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
              >
                Yes, End Assignment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Modal */}
      <ServiceModal
        isOpen={showServiceModal}
        onClose={closeServiceModal}
        onConfirm={handleServiceConfirm}
        equipmentItem={selectedEquipmentForService}
        serviceCompanies={serviceCompanies}
        mode={serviceModalMode}
      />

      {/* Report Generation Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-slate-800">
                  Generate Equipment Report
                </h3>
                <div className="mt-2 text-sm text-slate-600">
                  <p>
                    Generate a report for <strong>{selectedItems.size}</strong> selected equipment item(s).
                  </p>
                  <p className="mt-2">
                    The report will include: Type, Brand, Model, Serial Number, Validity Date, Inspection Date, Company, User Assignment, Project Assignment, and Status.
                  </p>
                  <p className="mt-2 font-semibold">
                    Choose report format:
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-3 mt-6">
              <button
                onClick={() => handleGenerateReport('excel')}
                disabled={isGeneratingReport}
                className="flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingReport ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Generate Excel Report
                  </>
                )}
              </button>

              <button
                onClick={() => handleGenerateReport('pdf')}
                disabled={isGeneratingReport}
                className="flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingReport ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Generate PDF Report
                  </>
                )}
              </button>

              <button
                onClick={() => setShowReportModal(false)}
                disabled={isGeneratingReport}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-steel-200 rounded-md hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Report Modal */}
      <ImportReportModal
        isOpen={showImportReportModal}
        onClose={handleCloseImportReport}
        reportData={importReportData}
      />
    </>
  );
};

export default EquipmentManagementNoGroup;